import AdminTable from '../../../components/admin/AdminTable';
import './kardex.css';
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    fetchCitasPorUsuarioCompleto,
    Cita,
    fetchPrecioPlanesServicios,
    PrecioPlanServicio,
} from '../../../services/citasService';
import { fetchUsuarioById, CustomUser } from '../../../services/userService';
import {
    fetchCitaCorrespondenciaPorCita,
} from '../../../services/cita-correspondencia';
import {
    fetchCorrespondenciasFiltradas,
    Correspondencia
} from '../../../services/correspondencia';
import {
    User, Mail, Phone, CreditCard,
    ArrowLeft, Activity, BarChart2,
    CheckCircle, AlertCircle, List as ClockIcon, XCircle,
    Users, Crown, TrendingUp, Calendar, DollarSign, Briefcase,
    Target, Filter, Search,
    ChevronDown, ExternalLink, MoreVertical,
    FileText, MessageSquare, History, MapPin, Building, Building2, Gift, Banknote,
    FileCheck, Eye, Camera,
    Package, Copy, Check, Send, Bell, UserCheck, Shield, KeyRound, Image as ImageIcon, QrCode, Sparkles, RefreshCw, CheckCircle2, ChevronRight, Share2, Info, ArrowUpRight, CheckSquare
} from 'lucide-react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Cell,
} from 'recharts';
import PagosModal from '../pagos/PagosModal';
import PagosModalHistorial from '../pagos/PagosModalHistorial';
import { fetchSesionesPorUsuario, SesionPOS } from '../../../services/pos/sesionPOSService';
import { OperadorPinDialog, useOperadorGrant } from '../../../components/pos/OperadorPinDialog';
import IniciarCorrespondenciaModal from '../correspondencia/IniciarCorrespondenciaModal';
import HistorialCorrespondenciaModal from '../correspondencia/HistorialCorrespondenciaModal';
import NuevaCorrespondenciaModal from '../correspondencia/NuevaCorrespondenciaModal';
import EditarCorrespondenciaModal from '../correspondencia/EditarCorrespondenciaModal';
import NotificarLlegadaModal from '../correspondencia/NotificarLlegadaModal';
import ValidarEntregaModal from '../correspondencia/ValidarEntregaModal';
import SucursalCitasModal from './SucursalCitasModal';
import GestionAfiliacionModal from '../afiliados/GestionAfiliacionModal';
import NuevaAfiliacionModal from '../afiliados/NuevaAfiliacionModal';
import PagoAfiliacionModal from '../afiliados/PagoAfiliacionModal';
import { Afiliacion } from '../../../services/afiliadosService';
import axiosInstance from '../../../services/axiosConfig';
import ModalVerDeclaracionJurada from '../../../components/Clients/ModalVerDeclaracionJurada';
import DeclaracionJuradaUpload from '../../../components/Upload/DeclaracionJuradaUpload';
import { fetchClientes, updateCliente, Cliente } from '../../../services/clienteService';

const brandColors = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryLight: '#8b3fa8',
    accent: '#ffd97c',
    accentDark: '#ea8406',
    dark: '#0F172A',
    darkLight: '#1E293B',
    grayDark: '#334155',
    gray: '#64748B',
    grayLight: '#CBD5E1',
    light: '#F8FAFC',
    white: '#FFFFFF'
};

const FOCUSABLE = 'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Partición de TODA la página por cliente de la ruta.
 *
 * El kardex se monta con ``key={usuarioId}``: al cambiar de cliente React
 * desmonta el árbol anterior completo (datos, stats, trazabilidad,
 * correspondencia, filtros, pestañas, selector de caja y todos los modales) y
 * monta uno nuevo. Ningún dato ni modal del cliente A puede renderizarse (ni
 * un frame) ni actuar bajo el cliente B; las continuaciones asíncronas de A
 * quedan además descartadas por sus propios guards de montaje/identidad.
 */
const DetalleKardexClientesPage = () => {
    const { usuarioId } = useParams<{ usuarioId: string }>();
    return <DetalleKardexClientesView key={usuarioId ?? ''} />;
};

const DetalleKardexClientesView = () => {
    const { usuarioId } = useParams<{ usuarioId: string }>();
    const navigate = useNavigate();
    const [usuario, setUsuario] = useState<CustomUser | null>(null);
    const [citas, setCitas] = useState<Cita[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [planes, setPlanes] = useState<PrecioPlanServicio[]>([]);
    const [modalPagoOpen, setModalPagoOpen] = useState(false);
    const [citaSeleccionada, setCitaSeleccionada] = useState<Cita | null>(null);
    // Caja EXPLÍCITA con la que se cobra desde el kardex (fuera del contexto
    // POS): el operador la elige entre sus cajas abiertas; nunca se infiere.
    const [sesionPosPago, setSesionPosPago] = useState<number | null>(null);
    // Cobrar desde el kardex (fuera del layout POS) exige el grant del operador
    // identificado por PIN sobre la caja elegida; cambiar de caja lo descarta.
    const operador = useOperadorGrant(sesionPosPago);
    const [sesionesAbiertas, setSesionesAbiertas] = useState<SesionPOS[]>([]);
    const [selectorSesionOpen, setSelectorSesionOpen] = useState(false);
    const [buscandoSesiones, setBuscandoSesiones] = useState(false);
    // Identidad del flujo de cobro: usuarioId (ruta) + citaId + generación +
    // montado. Cambiar de cliente, cerrar el selector/modal, navegar o
    // desmontar incrementa la generación y limpia selector/modal/cita/sesión;
    // toda continuación (tras cada await, en catch y finally) la comprueba.
    const pagoGenerationRef = useRef(0);
    const mountedRef = useRef(true);
    const usuarioIdRef = useRef(usuarioId);
    usuarioIdRef.current = usuarioId;
    const citaPagoRef = useRef<number | null>(null);
    const previousUsuarioIdRef = useRef(usuarioId);
    const selectorDialogRef = useRef<HTMLDivElement | null>(null);
    const selectorPreviousFocusRef = useRef<HTMLElement | null>(null);
    const [modalListadoPagosOpen, setModalListadoPagosOpen] = useState(false);
    const [tabActiva, setTabActiva] = useState<'todas' | 'titular' | 'asociadas'>('todas');
    const [busqueda, setBusqueda] = useState('');
    const [mostrarFiltros, setMostrarFiltros] = useState(false);
    const [citasConCorrespondencia, setCitasConCorrespondencia] = useState<Cita[]>([]);
    const [tabPrincipalActiva, setTabPrincipalActiva] = useState<'reservas' | 'correspondencia' | 'domiciliacion' | 'trazabilidad'>('reservas');
    const [modalIniciarCorrespondenciaOpen, setModalIniciarCorrespondenciaOpen] = useState(false);
    const [modalHistorialCorrespondenciaOpen, setModalHistorialCorrespondenciaOpen] = useState(false);
    const [citaSeleccionadaCorrespondencia, setCitaSeleccionadaCorrespondencia] = useState<Cita | null>(null);

    // Estado Correspondencia Detallada
    const [correspondencias, setCorrespondencias] = useState<Correspondencia[]>([]);
    const [loadingCorrespondencias, setLoadingCorrespondencias] = useState(false);
    const [filtroEstadoCorrespondencia, setFiltroEstadoCorrespondencia] = useState<string>('todos');
    const [busquedaCorrespondencia, setBusquedaCorrespondencia] = useState('');
    const [modalNuevaCorrespondenciaOpen, setModalNuevaCorrespondenciaOpen] = useState(false);
    const [modalEditarCorrespondenciaOpen, setModalEditarCorrespondenciaOpen] = useState(false);
    const [modalNotificarLlegadaOpen, setModalNotificarLlegadaOpen] = useState(false);
    const [modalValidarEntregaOpen, setModalValidarEntregaOpen] = useState(false);
    const [correspondenciaSeleccionadaObj, setCorrespondenciaSeleccionadaObj] = useState<Correspondencia | null>(null);
    const [verFotoModalUrl, setVerFotoModalUrl] = useState<string | null>(null);
    const [copiadoId, setCopiadoId] = useState<number | null>(null);
    const [copiadoPinId, setCopiadoPinId] = useState<number | null>(null);

    // Estado Afiliación, Domiciliación & Miro
    const [afiliacionCliente, setAfiliacionCliente] = useState<Afiliacion | null>(null);
    const [modalGestionAfiliacionOpen, setModalGestionAfiliacionOpen] = useState(false);
    const [modalNuevaAfiliacionOpen, setModalNuevaAfiliacionOpen] = useState(false);
    const [modalPagoAfiliacionOpen, setModalPagoAfiliacionOpen] = useState(false);


    const [modalSucursalCitasOpen, setModalSucursalCitasOpen] = useState(false);
    const [sucursalSeleccionada, setSucursalSeleccionada] = useState<{
        nombre: string;
        citas: Array<{
            citaId: number;
            codigo: string;
            fecha: string;
            hora: string;
            servicio: string;
            estado: string;
            monto: number;
        }>;
    } | null>(null);

    // Estado para Cliente y Declaración Jurada
    const [clienteObj, setClienteObj] = useState<Cliente | null>(null);
    const [modalVerDeclaracionOpen, setModalVerDeclaracionOpen] = useState(false);
    const [modalSubirDeclaracionOpen, setModalSubirDeclaracionOpen] = useState(false);
    const [archivoDeclaracion, setArchivoDeclaracion] = useState<File | null>(null);
    const [guardandoDeclaracion, setGuardandoDeclaracion] = useState(false);

    const [stats, setStats] = useState({
        totalCitas: 0,
        totalPagado: 0,
        servicioMasUsado: '',
        usoPorServicio: {} as Record<string, { count: number; total: number }>,
        deudaTotal: 0,
        totalCitasValor: 0,
        totalCitasTitular: 0,
        totalCitasAsociadas: 0,
        totalPagadoTitular: 0,
        totalPagadoAsociadas: 0,
        deudaCitas: 0,
        deudaAfiliacion: 0,
        totalPagadoCitas: 0,
        totalPagadoAfiliacion: 0,
        totalAfiliacionValor: 0,
    });

    const [statsSucursales, setStatsSucursales] = useState({
        sucursalesVisitadas: [] as Array<{
            sucursalId: number;
            nombre: string;
            visitas: number;
            ultimaVisita: string;
            citas: Array<{
                citaId: number;
                codigo: string;
                fecha: string;
                hora: string;
                servicio: string;
            }>;
        }>,
        ultimaSucursal: null as { id: number; nombre: string; fecha: string; citaCodigo: string } | null,
        totalSucursalesDiferentes: 0,
        historialCompleto: [] as Array<{
            citaId: number;
            codigo: string;
            sucursal: string;
            fecha: string;
            hora: string;
            servicio: string;
            estado: string;
            monto: number;
        }>,
    });

    const determinarRolCita = (cita: Cita, usuarioActual: CustomUser | null): string => {
        if (!usuarioActual) return 'Desconocido';
        const idUsuario = usuarioActual.id;

        if (cita.cliente === idUsuario) return 'Titular';
        if (cita.es_asociado) return 'Asociado';

        if (cita.clientes_asociados && Array.isArray(cita.clientes_asociados)) {
            if (cita.clientes_asociados.includes(idUsuario)) return 'Asociado';
        }

        if (cita.clientes_asociados_info && Array.isArray(cita.clientes_asociados_info)) {
            const esAsociado = cita.clientes_asociados_info.some(
                (asociado: any) => asociado.id === idUsuario
            );
            if (esAsociado) return 'Asociado';
        }

        return 'Desconocido';
    };

    const getRolBadge = (rol: string) => {
        switch (rol) {
            case 'Titular':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#c1348b]/10 to-[#8f1f64]/10 text-[#8f1f64] border border-[#c1348b]/20">
                        <Crown className="mr-1.5 h-3 w-3" />
                        Titular
                    </span>
                );
            case 'Asociado':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#61257b]/10 to-[#8b3fa8]/10 text-[#61257b] border border-[#8b3fa8]/20">
                        <Users className="mr-1.5 h-3 w-3" />
                        Asociado
                    </span>
                );
            default:
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        {rol}
                    </span>
                );
        }
    };

    const procesarTrazabilidadSucursales = useCallback((todasLasCitas: Cita[], planesData: PrecioPlanServicio[]) => {
        const sucursalesMap: {
            [key: number]: {
                nombre: string;
                visitas: number;
                ultimaFecha: string;
                ultimaCitaCodigo: string;
                citas: Array<{
                    citaId: number;
                    codigo: string;
                    fecha: string;
                    hora: string;
                    servicio: string;
                    estado: string;
                    monto: number;
                }>;
            }
        } = {};

        todasLasCitas.forEach(cita => {
            if (cita.sucursal_info) {
                const sucursalId = cita.sucursal_info.id;
                const fechaCita = cita.fecha_inicio ? new Date(cita.fecha_inicio).toISOString() : '';

                if (!sucursalesMap[sucursalId]) {
                    sucursalesMap[sucursalId] = {
                        nombre: cita.sucursal_info.nombre,
                        visitas: 0,
                        ultimaFecha: '',
                        ultimaCitaCodigo: '',
                        citas: []
                    };
                }

                sucursalesMap[sucursalId].visitas++;

                if (fechaCita > sucursalesMap[sucursalId].ultimaFecha || sucursalesMap[sucursalId].ultimaFecha === '') {
                    sucursalesMap[sucursalId].ultimaFecha = fechaCita;
                    sucursalesMap[sucursalId].ultimaCitaCodigo = cita.codigo;
                }

                const plan = planesData.find(p => p.id === cita.precio_plan_servicio);
                const nombreServicio = plan ? `${plan.servicio_nombre} - ${plan.plan_nombre}` : 'Desconocido';

                sucursalesMap[sucursalId].citas.push({
                    citaId: cita.id,
                    codigo: cita.codigo,
                    fecha: cita.fecha_inicio ? new Date(cita.fecha_inicio).toLocaleDateString('es-BO', { timeZone: 'UTC' }) : 'N/A',
                    hora: cita.hora_inicio || 'N/A',
                    servicio: nombreServicio,
                    estado: cita.estado,
                    monto: cita.cliente === usuario?.id ? Number(cita.total) || 0 : 0
                });
            }
        });

        const sucursalesVisitadas = Object.entries(sucursalesMap).map(([id, data]) => ({
            sucursalId: parseInt(id),
            nombre: data.nombre,
            visitas: data.visitas,
            ultimaVisita: data.ultimaFecha ? new Date(data.ultimaFecha).toLocaleDateString('es-BO', { timeZone: 'UTC' }) : 'N/A',
            citas: data.citas
        })).sort((a, b) => b.visitas - a.visitas);

        let ultimaSucursal = null;
        if (todasLasCitas.length > 0) {
            const citasOrdenadas = [...todasLasCitas].sort((a, b) => {
                const fechaA = a.fecha_inicio ? new Date(a.fecha_inicio).getTime() : 0;
                const fechaB = b.fecha_inicio ? new Date(b.fecha_inicio).getTime() : 0;
                return fechaB - fechaA;
            });

            const ultimaCitaConSucursal = citasOrdenadas.find(cita => cita.sucursal_info);

            if (ultimaCitaConSucursal && ultimaCitaConSucursal.sucursal_info) {
                ultimaSucursal = {
                    id: ultimaCitaConSucursal.sucursal_info.id,
                    nombre: ultimaCitaConSucursal.sucursal_info.nombre,
                    fecha: ultimaCitaConSucursal.fecha_inicio
                        ? new Date(ultimaCitaConSucursal.fecha_inicio).toLocaleDateString('es-BO', { timeZone: 'UTC' })
                        : 'N/A',
                    citaCodigo: ultimaCitaConSucursal.codigo
                };
            }
        }

        const historialCompleto: Array<{
            citaId: number;
            codigo: string;
            sucursal: string;
            fecha: string;
            hora: string;
            servicio: string;
            estado: string;
            monto: number;
        }> = [];

        todasLasCitas.forEach(cita => {
            if (cita.sucursal_info) {
                const plan = planesData.find(p => p.id === cita.precio_plan_servicio);
                const nombreServicio = plan ? `${plan.servicio_nombre} - ${plan.plan_nombre}` : 'Desconocido';

                historialCompleto.push({
                    citaId: cita.id,
                    codigo: cita.codigo,
                    sucursal: cita.sucursal_info.nombre,
                    fecha: cita.fecha_inicio ? new Date(cita.fecha_inicio).toLocaleDateString('es-BO', { timeZone: 'UTC' }) : 'N/A',
                    hora: cita.hora_inicio || 'N/A',
                    servicio: nombreServicio,
                    estado: cita.estado,
                    monto: cita.cliente === usuario?.id ? Number(cita.total) || 0 : 0
                });
            }
        });

        historialCompleto.sort((a, b) => {
            if (a.fecha === 'N/A' && b.fecha === 'N/A') return 0;
            if (a.fecha === 'N/A') return 1;
            if (b.fecha === 'N/A') return -1;

            const dateA = new Date(a.fecha.split('/').reverse().join('-'));
            const dateB = new Date(b.fecha.split('/').reverse().join('-'));
            return dateB.getTime() - dateA.getTime();
        });

        setStatsSucursales({
            sucursalesVisitadas,
            ultimaSucursal,
            totalSucursalesDiferentes: sucursalesVisitadas.length,
            historialCompleto
        });
    }, []);

    const invalidarFlujoPago = useCallback(() => {
        // Nada del flujo de cobro anterior sobrevive: ni selector, ni modal,
        // ni cita, ni sesión, ni el estado "buscando cajas".
        pagoGenerationRef.current += 1;
        citaPagoRef.current = null;
        setSelectorSesionOpen(false);
        setModalPagoOpen(false);
        setCitaSeleccionada(null);
        setSesionPosPago(null);
        setSesionesAbiertas([]);
        setBuscandoSesiones(false);
    }, []);

    const recargarCorrespondencias = useCallback(async (userId?: number) => {
        const targetId = userId || (usuario ? usuario.id : (usuarioId ? parseInt(usuarioId) : null));
        if (!targetId) return;
        setLoadingCorrespondencias(true);
        try {
            const data = await fetchCorrespondenciasFiltradas({ cliente: targetId });
            setCorrespondencias(data || []);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoadingCorrespondencias(false);
        }
    }, [usuario, usuarioId]);

    const recargarAfiliacion = useCallback(async () => {
        const targetId = usuario ? usuario.id : (usuarioId ? parseInt(usuarioId) : null);
        if (!targetId) return;
        try {
            const resAf = await axiosInstance.get(`/afiliaciones/?cliente=${targetId}`);
            const listaAf = resAf.data?.results || resAf.data || [];
            const af = listaAf.length > 0 ? listaAf[0] : null;
            setAfiliacionCliente(af || null);

            let afMontoTotal = 0;
            let afMontoPagado = 0;
            let afDeuda = 0;
            if (af && !af.cita) {
                afMontoTotal = Number(af.monto_total || 0);
                afMontoPagado = Number(af.monto_pagado || 0);
                afDeuda = Math.max(0, afMontoTotal - afMontoPagado);
            }

            setStats(prev => ({
                ...prev,
                totalPagado: prev.totalPagadoCitas + afMontoPagado,
                deudaTotal: prev.deudaCitas + afDeuda,
                totalCitasValor: prev.totalCitasValor - prev.totalAfiliacionValor + afMontoTotal,
                deudaAfiliacion: afDeuda,
                totalPagadoAfiliacion: afMontoPagado,
                totalAfiliacionValor: afMontoTotal,
            }));
        } catch (err) {
            /* Browser diagnostics omitted. */
        }
    }, [usuario, usuarioId]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            pagoGenerationRef.current += 1;
            citaPagoRef.current = null;
        };
    }, []);

    // Cambio de cliente en la ruta: reinicia el render antes de que React pueda
    // confirmar un frame con el selector/modal y la cita del cliente anterior.
    // Un useEffect sería demasiado tarde para una transición financiera.
    // (Defensa en profundidad: el wrapper ya remonta toda la vista por
    // ``key={usuarioId}``, así que esta rama solo actúa si la vista se usara
    // sin él.)
    if (previousUsuarioIdRef.current !== usuarioId) {
        previousUsuarioIdRef.current = usuarioId;
        invalidarFlujoPago();
    }

    useEffect(() => {
        // La carga está ligada al usuarioId vigente: una respuesta tardía de
        // otro cliente nunca pisa los datos del actual.
        let cancelled = false;
        const live = () => !cancelled && mountedRef.current && usuarioIdRef.current === usuarioId;
        const loadData = async () => {
            setIsLoading(true);
            setUsuario(null);
            setCitas([]);
            setCitasConCorrespondencia([]);
            try {
                if (usuarioId) {
                    const [usuarioData, clientesList] = await Promise.all([
                        fetchUsuarioById(parseInt(usuarioId)),
                        fetchClientes()
                    ]);
                    if (!live()) return;
                    setUsuario(usuarioData);

                    const foundCliente = clientesList.find((c: Cliente) => c.usuario?.id === usuarioData.id);
                    if (foundCliente) {
                        setClienteObj(foundCliente);
                    }

                    const todasLasCitas = await fetchCitasPorUsuarioCompleto(usuarioData.id);
                    if (!live()) return;
                    setCitas(todasLasCitas);

                    const listaPlanes = await fetchPrecioPlanesServicios();
                    if (!live()) return;
                    setPlanes(listaPlanes);

                    let afMontoTotal = 0;
                    let afMontoPagado = 0;
                    let afDeuda = 0;
                    try {
                        const resAf = await axiosInstance.get(`/afiliaciones/?cliente=${usuarioData.id}`);
                        const listaAf = resAf.data?.results || resAf.data || [];
                        const encontrada = listaAf.length > 0 ? listaAf[0] : null;
                        setAfiliacionCliente(encontrada || null);
                        if (encontrada) {
                            afMontoTotal = encontrada.cita ? 0 : Number(encontrada.monto_total || 0);
                            afMontoPagado = encontrada.cita ? 0 : Number(encontrada.monto_pagado || 0);
                            afDeuda = Math.max(0, afMontoTotal - afMontoPagado);
                        }
                    } catch (err) {
                        /* Browser diagnostics omitted. */
                    }

                    procesarTrazabilidadSucursales(todasLasCitas, listaPlanes);

                    const citasConCorrespondenciaList: Cita[] = [];
                    for (const cita of todasLasCitas) {
                        try {
                            const correspondenciaConfig = await fetchCitaCorrespondenciaPorCita(cita.id);
                            if (!live()) return;
                            if (correspondenciaConfig && correspondenciaConfig.habilitado) {
                                citasConCorrespondenciaList.push(cita);
                            }
                        } catch (error) {
                            if (!live()) return;
                            /* Browser diagnostics omitted. */
                        }
                    }
                    setCitasConCorrespondencia(citasConCorrespondenciaList);
                    await recargarCorrespondencias(usuarioData.id);

                    const citasTitular: Cita[] = [];
                    const citasAsociadas: Cita[] = [];
                    const conteo: Record<string, { count: number; total: number }> = {};
                    let totalPagadoCitas = 0;
                    let deudaCitas = 0;
                    let totalCitasValor = 0;
                    let totalPagadoTitular = 0;
                    let totalPagadoAsociadas = 0;

                    todasLasCitas.forEach(cita => {
                        const plan = listaPlanes.find(p => p.id === cita.precio_plan_servicio);
                        const nombre = plan ? `${plan.servicio_nombre} - ${plan.plan_nombre}` : 'Desconocido';

                        if (!conteo[nombre]) conteo[nombre] = { count: 0, total: 0 };
                        conteo[nombre].count++;
                        conteo[nombre].total += cita.cliente === usuarioData.id ? Number(cita.total) || 0 : 0;

                        if (cita.cliente === usuarioData.id) {
                        totalCitasValor += Number(cita.total) || 0;
                        totalPagadoCitas += Number(cita.monto_pagado) || 0;
                        deudaCitas += Math.max(0, Number(cita.total) - (Number(cita.monto_pagado) || 0));

                        }
                        const rol = determinarRolCita(cita, usuarioData);
                        if (rol === 'Titular') {
                            citasTitular.push(cita);
                            totalPagadoTitular += Number(cita.monto_pagado) || 0;
                        } else if (rol === 'Asociado') {
                            citasAsociadas.push(cita);
                            totalPagadoAsociadas += 0;
                        }
                    });

                    const servicioMasUsado = Object.entries(conteo)
                        .sort((a, b) => b[1].count - a[1].count)[0]?.[0] ?? 'Sin datos';

                    const statsData = {
                        totalCitas: todasLasCitas.length,
                        totalPagado: totalPagadoCitas + afMontoPagado,
                        servicioMasUsado,
                        usoPorServicio: conteo,
                        deudaTotal: deudaCitas + afDeuda,
                        totalCitasValor: totalCitasValor + afMontoTotal,
                        totalCitasTitular: citasTitular.length,
                        totalCitasAsociadas: citasAsociadas.length,
                        totalPagadoTitular,
                        totalPagadoAsociadas,
                        deudaCitas,
                        deudaAfiliacion: afDeuda,
                        totalPagadoCitas,
                        totalPagadoAfiliacion: afMontoPagado,
                        totalAfiliacionValor: afMontoTotal,
                    };

                    setStats(statsData);
                }
            } catch (error) {
                if (!live()) return;
                /* Browser diagnostics omitted. */
            } finally {
                if (live()) setIsLoading(false);
            }
        };

        loadData();
        return () => { cancelled = true; };
    }, [usuarioId, procesarTrazabilidadSucursales]);

    const citasFiltradas = useMemo(() => {
        if (!usuario) return [];

        let filtered = citas.filter(cita => {
            const rol = determinarRolCita(cita, usuario);

            switch (tabActiva) {
                case 'titular':
                    return rol === 'Titular';
                case 'asociadas':
                    return rol === 'Asociado';
                case 'todas':
                default:
                    return rol === 'Titular' || rol === 'Asociado';
            }
        });

        if (busqueda.trim()) {
            const searchTerm = busqueda.toLowerCase();
            filtered = filtered.filter(cita => {
                const plan = planes.find(p => p.id === cita.precio_plan_servicio);
                const nombreServicio = plan
                    ? `${plan.servicio_nombre} ${plan.plan_nombre}`.toLowerCase()
                    : '';

                return (
                    cita.codigo.toLowerCase().includes(searchTerm) ||
                    nombreServicio.includes(searchTerm) ||
                    cita.estado.toLowerCase().includes(searchTerm) ||
                    cita.estado_pago.toLowerCase().includes(searchTerm)
                );
            });
        }

        return filtered.sort((a, b) => b.id - a.id);
    }, [citas, tabActiva, usuario, busqueda, planes]);

    const chartData = Object.entries(stats.usoPorServicio).map(([nombre, datos]) => ({
        nombre_servicio: nombre,
        cantidad_usos: datos.count
    })).sort((a, b) => b.cantidad_usos - a.cantidad_usos);

    const servicioMasUsadoData = useMemo(() => {
        if (!stats.servicioMasUsado || stats.servicioMasUsado === 'Sin datos') return null;

        const servicioData = stats.usoPorServicio[stats.servicioMasUsado];

        if (!servicioData || servicioData.count === undefined) return null;

        return {
            nombre: stats.servicioMasUsado,
            cantidad: servicioData.count
        };
    }, [stats]);

    const getEstadoBadge = (estado: string) => {
        switch (estado.toLowerCase()) {
            case 'confirmada':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#8b3fa8]/10 to-[#61257b]/10 text-[#61257b] border border-[#8b3fa8]/20">
                        <CheckCircle className="mr-1.5 h-3 w-3" />
                        Confirmada
                    </span>
                );
            case 'pendiente':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#ffd97c]/10 to-[#ea8406]/10 text-[#ea8406] border border-[#ffd97c]/20">
                        <ClockIcon className="mr-1.5 h-3 w-3" />
                        Pendiente
                    </span>
                );
            case 'cancelada':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#c1348b]/10 to-[#8f1f64]/10 text-[#8f1f64] border border-[#c1348b]/20">
                        <XCircle className="mr-1.5 h-3 w-3" />
                        Cancelada
                    </span>
                );
            default:
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        {estado}
                    </span>
                );
        }
    };

    const getEstadoPagoBadge = (estado: string) => {
        switch (estado.toLowerCase()) {
            case 'pagado completo':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#8b3fa8]/10 to-[#61257b]/10 text-[#61257b] border border-[#8b3fa8]/20">
                        <CheckCircle className="mr-1.5 h-3 w-3" />
                        Pagado
                    </span>
                );
            case 'pagado parcial':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#ffd97c]/10 to-[#ea8406]/10 text-[#ea8406] border border-[#ffd97c]/20">
                        <AlertCircle className="mr-1.5 h-3 w-3" />
                        Parcial
                    </span>
                );
            case 'pendiente':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gradient-to-r from-[#c1348b]/10 to-[#8f1f64]/10 text-[#8f1f64] border border-[#c1348b]/20">
                        <XCircle className="mr-1.5 h-3 w-3" />
                        Pendiente
                    </span>
                );
            default:
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                        {estado}
                    </span>
                );
        }
    };

    const abrirModalSucursalCitas = (sucursal: any) => {
        const historialFiltrado = statsSucursales.historialCompleto
            .filter(item => {
                const sucursalItem = statsSucursales.sucursalesVisitadas
                    .find(s => s.nombre === item.sucursal);
                return sucursalItem?.sucursalId === sucursal.sucursalId;
            });

        setSucursalSeleccionada({
            nombre: sucursal.nombre,
            citas: historialFiltrado
        });
        setModalSucursalCitasOpen(true);
    };

    const handleAbrirModalIniciarCorrespondencia = (cita: Cita) => {
        setCitaSeleccionadaCorrespondencia(cita);
        setModalIniciarCorrespondenciaOpen(true);
    };

    const handleAbrirModalHistorialCorrespondencia = (cita: Cita) => {
        setCitaSeleccionadaCorrespondencia(cita);
        setModalHistorialCorrespondenciaOpen(true);
    };

    const abrirModalPago = async (cita: Cita) => {
        // Identidad capturada ANTES del await: usuario de la ruta + cita +
        // generación. La cita debe pertenecer al cliente cargado en pantalla.
        if (!usuario || !usuarioId || String(usuario.id) !== usuarioId || cita.cliente !== usuario.id) return;
        const generation = ++pagoGenerationRef.current;
        const usuarioRuta = usuarioId;
        const citaId = cita.id;
        citaPagoRef.current = citaId;
        const isCurrent = () => mountedRef.current
            && generation === pagoGenerationRef.current
            && usuarioIdRef.current === usuarioRuta
            && citaPagoRef.current === citaId;
        selectorPreviousFocusRef.current = document.activeElement as HTMLElement | null;
        setSelectorSesionOpen(false);
        setModalPagoOpen(false);
        setCitaSeleccionada(cita);
        setSesionPosPago(null);
        setSesionesAbiertas([]);
        setBuscandoSesiones(true);
        try {
            const abiertas = (await fetchSesionesPorUsuario()).filter(s => s.estado === 'abierta');
            if (!isCurrent()) return;
            if (abiertas.length === 0) {
                alert('No tienes una sesión POS abierta. Abre una caja antes de registrar pagos.');
                return;
            }
            if (abiertas.length === 1) {
                setSesionPosPago(abiertas[0].id);
                setModalPagoOpen(true);
                return;
            }
            setSesionesAbiertas(abiertas);
            setSelectorSesionOpen(true);
        } catch {
            if (!isCurrent()) return;
            alert('No se pudo verificar tu sesión POS.');
        } finally {
            if (isCurrent()) setBuscandoSesiones(false);
        }
    };

    const elegirSesionParaPago = (sesionId: number) => {
        // Solo la cita del flujo vigente puede recibir la caja elegida.
        if (!selectorSesionOpen || !citaSeleccionada || citaPagoRef.current !== citaSeleccionada.id) return;
        if (!sesionesAbiertas.some(s => s.id === sesionId)) return;
        setSelectorSesionOpen(false);
        setSesionPosPago(sesionId);
        setModalPagoOpen(true);
    };

    const cerrarSelectorSesion = useCallback(() => {
        invalidarFlujoPago();
    }, [invalidarFlujoPago]);

    const cerrarModalPago = () => {
        invalidarFlujoPago();
    };

    // Foco inicial, Escape, trampa/restauración de foco y bloqueo de scroll
    // del selector de caja (diálogo modal accesible).
    useEffect(() => {
        if (!selectorSesionOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const focusTimer = window.setTimeout(() => {
            const dialog = selectorDialogRef.current;
            (dialog?.querySelector<HTMLElement>(FOCUSABLE) || dialog)?.focus();
        }, 0);
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                cerrarSelectorSesion();
                return;
            }
            const dialog = selectorDialogRef.current;
            if (event.key !== 'Tab' || !dialog) return;
            const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
            if (!focusable.length) { event.preventDefault(); dialog.focus(); return; }
            const first = focusable[0], last = focusable[focusable.length - 1];
            const active = document.activeElement as HTMLElement | null;
            if (!dialog.contains(active)) { event.preventDefault(); first.focus(); return; }
            if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
            const previous = selectorPreviousFocusRef.current;
            if (previous && previous.isConnected && typeof previous.focus === 'function') previous.focus();
        };
    }, [selectorSesionOpen, cerrarSelectorSesion]);

    const recargarPagos = async () => {
        // La recarga pertenece al cliente de la ruta: se descarta si cambió
        // o si la página se desmontó mientras respondía.
        if (usuario && citaSeleccionada && usuarioId && String(usuario.id) === usuarioId) {
            const usuarioRuta = usuarioId;
            const todasLasCitas = await fetchCitasPorUsuarioCompleto(usuario.id);
            if (!mountedRef.current || usuarioIdRef.current !== usuarioRuta) return;
            setCitas(todasLasCitas);

            let afMontoTotal = 0;
            let afMontoPagado = 0;
            let afDeuda = 0;
            if (afiliacionCliente && !afiliacionCliente.cita) {
                afMontoTotal = Number(afiliacionCliente.monto_total || 0);
                afMontoPagado = Number(afiliacionCliente.monto_pagado || 0);
                afDeuda = Math.max(0, afMontoTotal - afMontoPagado);
            }

            let nuevoTotalCitasValor = 0;
            let nuevoTotalPagadoCitas = 0;
            let nuevaDeudaCitas = 0;
            let nuevoTotalPagadoTitular = 0;
            let nuevoTotalPagadoAsociadas = 0;

            todasLasCitas.forEach(cita => {
                if (cita.cliente === usuario.id) {
                nuevoTotalCitasValor += Number(cita.total) || 0;
                nuevoTotalPagadoCitas += Number(cita.monto_pagado) || 0;
                nuevaDeudaCitas += Math.max(0, Number(cita.total) - (Number(cita.monto_pagado) || 0));

                }
                const rol = determinarRolCita(cita, usuario);
                if (rol === 'Titular') {
                    nuevoTotalPagadoTitular += Number(cita.monto_pagado) || 0;
                } else if (rol === 'Asociado') {
                    nuevoTotalPagadoAsociadas += 0;
                }
            });

            setStats(prev => ({
                ...prev,
                totalPagado: nuevoTotalPagadoCitas + afMontoPagado,
                deudaTotal: nuevaDeudaCitas + afDeuda,
                totalCitasValor: nuevoTotalCitasValor + afMontoTotal,
                totalPagadoTitular: nuevoTotalPagadoTitular,
                totalPagadoAsociadas: nuevoTotalPagadoAsociadas,
                deudaCitas: nuevaDeudaCitas,
                deudaAfiliacion: afDeuda,
                totalPagadoCitas: nuevoTotalPagadoCitas,
                totalPagadoAfiliacion: afMontoPagado,
                totalAfiliacionValor: afMontoTotal,
            }));
        }
    };

    const handleCitaClick = (citaId: number) => {
        navigate(`/dashboard/kardex-clientes/sesiones/${citaId}?usuarioId=${usuarioId}`);
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                    <p className="font-semibold text-gray-800">{label}</p>
                    <p className="text-[#c1348b] font-semibold">
                        Usos: <span className="text-gray-800">{payload[0].value}</span>
                    </p>
                </div>
            );
        }
        return null;
    };

    const sucursalesChartData = statsSucursales.sucursalesVisitadas.map(s => ({
        nombre: s.nombre,
        visitas: s.visitas
    }));

    const renderContenidoReservas = () => (
        <div className="kd-section overflow-hidden">
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Historial de reservas</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            {citasFiltradas.length} reservas encontradas
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por código, servicio..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] text-sm w-full sm:w-64"
                            />
                        </div>
                        <button
                            onClick={() => setMostrarFiltros(!mostrarFiltros)}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 flex items-center gap-2"
                        >
                            <Filter className="h-4 w-4" />
                            Filtros
                            <ChevronDown className={`h-4 w-4 transition-transform ${mostrarFiltros ? 'rotate-180' : ''}`} />
                        </button>
                    </div>
                </div>

                <div className={`${mostrarFiltros ? 'block' : 'hidden'} mb-6`}>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex flex-wrap gap-3">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Filtrar por:</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                    <button
                        onClick={() => setTabActiva('todas')}
                        className={`px-4 py-2 text-sm font-medium rounded transition-all duration-200 ${tabActiva === 'todas'
                            ? 'bg-white text-gray-800 shadow-sm'
                            : 'text-gray-600 hover:text-gray-800'
                            }`}
                    >
                        Todas ({citas.length})
                    </button>
                    <button
                        onClick={() => setTabActiva('titular')}
                        className={`px-4 py-2 text-sm font-medium rounded transition-all duration-200 ${tabActiva === 'titular'
                            ? 'bg-gradient-to-r from-[#c1348b]/10 to-[#8f1f64]/10 text-[#8f1f64]'
                            : 'text-gray-600 hover:text-gray-800'
                            }`}
                    >
                        Titular ({stats.totalCitasTitular})
                    </button>
                    <button
                        onClick={() => setTabActiva('asociadas')}
                        className={`px-4 py-2 text-sm font-medium rounded transition-all duration-200 ${tabActiva === 'asociadas'
                            ? 'bg-gradient-to-r from-[#61257b]/10 to-[#8b3fa8]/10 text-[#61257b]'
                            : 'text-gray-600 hover:text-gray-800'
                            }`}
                    >
                        Asociado ({stats.totalCitasAsociadas})
                    </button>
                </div>
            </div>

            {citasFiltradas.length > 0 ? (
                <div className="p-4 sm:p-6">
                    <div className="grid gap-3">
                        {citasFiltradas.map((cita) => {
                            const plan = planes.find(p => p.id === cita.precio_plan_servicio);
                            const nombreServicio = plan
                                ? `${plan.servicio_nombre} - ${plan.plan_nombre}`
                                : 'Desconocido';
                            const rol = determinarRolCita(cita, usuario);

                            return (
                                <div
                                    key={cita.id}
                                    className="kd-reservation transition-colors"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        {getRolBadge(rol)}
                                                        <span className="text-sm font-mono text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                                            {cita.codigo}
                                                        </span>
                                                    </div>
                                                    <h4 className="font-medium text-gray-800">{nombreServicio}</h4>
                                                </div>
                                                <button className="text-gray-400 hover:text-gray-600">
                                                    <MoreVertical className="h-5 w-5" />
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                <div>
                                                    <p className="text-xs text-gray-500">Fecha</p>
                                                    <p className="text-sm font-medium text-gray-800">
                                                        {new Date(cita.fecha_inicio!).toLocaleDateString('es-BO', { timeZone: 'UTC' })}
                                                    </p>
                                                    <p className="text-xs text-gray-600">{cita.hora_inicio}</p>
                                                </div>

                                                <div>
                                                    <p className="text-xs text-gray-500">Estado</p>
                                                    <div className="mt-1">
                                                        {getEstadoBadge(cita.estado)}
                                                    </div>
                                                </div>

                                                {rol === 'Titular' && <div>
                                                    <p className="text-xs text-gray-500">Monto</p>
                                                    <div className="space-y-1">
                                                        <p className="text-sm font-semibold text-gray-800">
                                                            Bs {Number(cita.total).toFixed(2)}
                                                        </p>
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-xs text-gray-600">Pagado:</span>
                                                            <span className="text-xs font-medium text-[#61257b]">
                                                                Bs {Number(cita.monto_pagado || 0).toFixed(2)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>}

                                                {rol === 'Titular' && <div>
                                                    <p className="text-xs text-gray-500">Pago</p>
                                                    <div className="mt-1">
                                                        {getEstadoPagoBadge(cita.estado_pago)}
                                                    </div>
                                                </div>}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            {rol === 'Titular' && <button
                                                onClick={() => handleCitaClick(cita.id)}
                                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                            >
                                                <ExternalLink className="mr-1.5 h-4 w-4" />
                                                Ver
                                            </button>}
                                            {rol === 'Titular' && <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    abrirModalPago(cita);
                                                }}
                                                disabled={buscandoSesiones}
                                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#61257b] to-[#8b3fa8] hover:shadow-md rounded-lg transition-all disabled:opacity-60"
                                            >
                                                <DollarSign className="mr-1.5 h-4 w-4" />
                                                Pagar
                                            </button>}
                                        </div>
                                    </div>

                                    {rol === 'Asociado' && cita.cliente_info && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                            <p className="text-xs text-gray-500">
                                                Titular: <span className="font-medium text-gray-700">
                                                    {cita.cliente_info.first_name} {cita.cliente_info.last_name}
                                                </span>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="text-center py-12">
                    <div className="mx-auto w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Calendar className="h-7 w-7 text-gray-400" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-800 mb-2">
                        {busqueda
                            ? 'No se encontraron resultados'
                            : tabActiva === 'todas'
                                ? 'No hay reservas registradas'
                                : tabActiva === 'titular'
                                    ? 'No hay reservas como titular'
                                    : 'No hay reservas como asociado'}
                    </h3>
                    <p className="text-gray-600 text-sm max-w-md mx-auto">
                        {busqueda
                            ? 'Intenta con otros términos de búsqueda'
                            : 'Este usuario no tiene reservas registradas con los filtros aplicados'}
                    </p>
                </div>
            )}
        </div>
    );

    const formatDateTime = (dateString: string | null | undefined): string => {
        if (!dateString) return 'Pendiente';
        const date = new Date(dateString);
        return date.toLocaleString('es-BO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    const copiarAlPortapapeles = (texto: string, id: number, tipo: 'codigo' | 'pin') => {
        navigator.clipboard.writeText(texto);
        if (tipo === 'codigo') {
            setCopiadoId(id);
            setTimeout(() => setCopiadoId(null), 2000);
        } else {
            setCopiadoPinId(id);
            setTimeout(() => setCopiadoPinId(null), 2000);
        }
    };

    const enviarNotificacionWhatsAppCliente = (item: Correspondencia) => {
        const nombreDest = item.destinatario_nombre || `${usuario?.first_name || ''} ${usuario?.last_name || ''}`.trim() || 'Estimado(a) Cliente';
        const telefono = item.destinatario_telefono || usuario?.telefono || clienteObj?.telefono || '';
        const codigo = item.codigo || `#${item.id}`;
        const pin = item.pin_seguridad || 'N/A';
        const urlSeguimiento = item.token_verificacion
            ? `${window.location.origin}/correspondencias/seguimiento-publico/${item.token_verificacion}`
            : '';

        let mensaje = `Hola *${nombreDest}*, te informamos desde *Co Marca Cowork* que tienes una correspondencia/encomienda registrada.\n\n`;
        mensaje += `📦 *Código de envío:* ${codigo}\n`;
        mensaje += `👤 *Remitente:* ${item.remitente_nombre} ${item.remitente_apellido || ''}\n`;
        if (item.descripcion) mensaje += `📝 *Detalle:* ${item.descripcion}\n`;
        mensaje += `🔐 *PIN de entrega:* ${pin}\n`;
        if (urlSeguimiento) {
            mensaje += `🔗 *Ver seguimiento en vivo:* ${urlSeguimiento}\n`;
        }
        mensaje += `\nPuedes retirarla en nuestra recepción presentando tu PIN de seguridad. ¡Que tengas un excelente día!`;

        const telLimpio = telefono.replace(/[^0-9]/g, '');
        const url = `https://wa.me/${telLimpio ? (telLimpio.length <= 8 ? `591${telLimpio}` : telLimpio) : ''}?text=${encodeURIComponent(mensaje)}`;
        window.open(url, '_blank');
    };

    const getEstadoCorrespondenciaBadge = (estado: string) => {
        switch (estado) {
            case 'recibido':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center gap-1.5 text-xs font-bold rounded-full bg-blue-50 text-blue-700 border border-blue-200 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        En Recepción
                    </span>
                );
            case 'notificado':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center gap-1.5 text-xs font-bold rounded-full bg-amber-50 text-amber-700 border border-amber-200 shadow-sm">
                        <Bell className="w-3.5 h-3.5 text-amber-600 animate-bounce" />
                        Notificado al Cliente
                    </span>
                );
            case 'entregado':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center gap-1.5 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-sm">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        Entregado con Éxito
                    </span>
                );
            case 'cancelado':
                return (
                    <span className="px-2.5 py-1 inline-flex items-center gap-1.5 text-xs font-bold rounded-full bg-red-50 text-red-700 border border-red-200 shadow-sm">
                        <XCircle className="w-3.5 h-3.5 text-red-500" />
                        Cancelado
                    </span>
                );
            default:
                return (
                    <span className="px-2.5 py-1 inline-flex items-center text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        {estado}
                    </span>
                );
        }
    };

    const correspondenciasFiltradas = useMemo(() => {
        let list = [...correspondencias];
        if (filtroEstadoCorrespondencia === 'activos') {
            list = list.filter(c => c.estado === 'recibido' || c.estado === 'notificado');
        } else if (filtroEstadoCorrespondencia !== 'todos') {
            list = list.filter(c => c.estado === filtroEstadoCorrespondencia);
        }
        if (busquedaCorrespondencia.trim()) {
            const q = busquedaCorrespondencia.toLowerCase();
            list = list.filter(c =>
                (c.codigo && c.codigo.toLowerCase().includes(q)) ||
                (c.remitente_nombre && c.remitente_nombre.toLowerCase().includes(q)) ||
                (c.remitente_apellido && c.remitente_apellido.toLowerCase().includes(q)) ||
                (c.destinatario_nombre && c.destinatario_nombre.toLowerCase().includes(q)) ||
                (c.descripcion && c.descripcion.toLowerCase().includes(q)) ||
                (c.pin_seguridad && c.pin_seguridad.includes(q)) ||
                (c.afiliacion_empresa && c.afiliacion_empresa.toLowerCase().includes(q))
            );
        }
        return list;
    }, [correspondencias, filtroEstadoCorrespondencia, busquedaCorrespondencia]);

    const statsCorrespondencia = useMemo(() => {
        return {
            total: correspondencias.length,
            recibidas: correspondencias.filter(c => c.estado === 'recibido').length,
            notificadas: correspondencias.filter(c => c.estado === 'notificado').length,
            entregadas: correspondencias.filter(c => c.estado === 'entregado').length,
            activas: correspondencias.filter(c => c.estado === 'recibido' || c.estado === 'notificado').length,
        };
    }, [correspondencias]);

    const renderContenidoCorrespondencia = () => (
        <div className="space-y-6">
            {/* Header del Tab con Acciones */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#61257b] to-[#8b3fa8] flex items-center justify-center text-white shadow-md">
                            <Package className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-gray-900">
                                    Kardex de Correspondencia y Encomiendas
                                </h3>
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#61257b]/10 text-[#61257b] border border-[#61257b]/20">
                                    {statsCorrespondencia.total} Envíos
                                </span>
                                {statsCorrespondencia.activas > 0 && (
                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-700 border border-amber-500/30 animate-pulse">
                                        {statsCorrespondencia.activas} en curso
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Seguimiento en tiempo real de sobres, paquetes y documentos recibidos en recepción para este cliente.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5">
                        <button
                            type="button"
                            onClick={() => recargarCorrespondencias()}
                            disabled={loadingCorrespondencias}
                            className="px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all flex items-center gap-1.5"
                            title="Recargar correspondencias"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loadingCorrespondencias ? 'animate-spin' : ''}`} />
                            Actualizar
                        </button>
                        <button
                            type="button"
                            onClick={() => setModalNuevaCorrespondenciaOpen(true)}
                            className="px-4 py-2 text-xs font-bold text-white bg-gradient-to-r from-[#c1348b] to-[#8f1f64] hover:opacity-95 rounded-xl transition-all shadow-sm flex items-center gap-1.5"
                        >
                            <Package className="w-4 h-4" />
                            + Registrar Correspondencia
                        </button>
                    </div>
                </div>

                {/* Métricas de Correspondencia */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-6 border-t border-gray-100">
                    <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/70">
                        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Total Registradas</div>
                        <div className="text-2xl font-black text-gray-900 mt-1">{statsCorrespondencia.total}</div>
                        <div className="text-[11px] text-gray-400 mt-0.5">Historial acumulado</div>
                    </div>
                    <div className="bg-blue-50/70 rounded-xl p-3.5 border border-blue-200/70">
                        <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider">En Recepción</div>
                        <div className="text-2xl font-black text-blue-800 mt-1">{statsCorrespondencia.recibidas}</div>
                        <div className="text-[11px] text-blue-600/80 mt-0.5">Pendientes de notificación</div>
                    </div>
                    <div className="bg-amber-50/70 rounded-xl p-3.5 border border-amber-200/70">
                        <div className="text-[11px] font-bold text-amber-700 uppercase tracking-wider">Notificadas</div>
                        <div className="text-2xl font-black text-amber-800 mt-1">{statsCorrespondencia.notificadas}</div>
                        <div className="text-[11px] text-amber-700/80 mt-0.5">Esperando retiro por cliente</div>
                    </div>
                    <div className="bg-emerald-50/70 rounded-xl p-3.5 border border-emerald-200/70">
                        <div className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">Entregadas</div>
                        <div className="text-2xl font-black text-emerald-800 mt-1">{statsCorrespondencia.entregadas}</div>
                        <div className="text-[11px] text-emerald-700/80 mt-0.5">Retiradas con éxito</div>
                    </div>
                </div>
            </div>

            {/* Barra de Filtros y Búsqueda */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Tabs de Filtro de Estado */}
                    <div className="flex flex-wrap items-center gap-1.5 p-1 bg-gray-100 rounded-xl">
                        {[
                            { id: 'todos', label: 'Todas', count: statsCorrespondencia.total },
                            { id: 'activos', label: 'En Proceso (Activas)', count: statsCorrespondencia.activas },
                            { id: 'recibido', label: 'En Recepción', count: statsCorrespondencia.recibidas },
                            { id: 'notificado', label: 'Notificadas', count: statsCorrespondencia.notificadas },
                            { id: 'entregado', label: 'Entregadas', count: statsCorrespondencia.entregadas },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setFiltroEstadoCorrespondencia(tab.id)}
                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${filtroEstadoCorrespondencia === tab.id
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                {tab.label}
                                <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${filtroEstadoCorrespondencia === tab.id
                                        ? 'bg-[#007FC8] text-white font-black'
                                        : 'bg-gray-200 text-gray-600'
                                    }`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Buscador en tiempo real */}
                    <div className="relative w-full md:w-80">
                        <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar por código, remitente, PIN..."
                            value={busquedaCorrespondencia}
                            onChange={(e) => setBusquedaCorrespondencia(e.target.value)}
                            className="w-full pl-10 pr-8 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#61257b]/30 focus:border-[#61257b] transition-all bg-gray-50/50 focus:bg-white"
                        />
                        {busquedaCorrespondencia && (
                            <button
                                type="button"
                                onClick={() => setBusquedaCorrespondencia('')}
                                className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold p-1"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Listado Principal de Correspondencias con Pipeline / Proceso */}
            {loadingCorrespondencias ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
                    <div className="w-10 h-10 border-4 border-[#61257b]/20 border-t-[#61257b] rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm text-gray-500 font-medium">Cargando correspondencias del cliente...</p>
                </div>
            ) : correspondenciasFiltradas.length > 0 ? (
                <div className="space-y-4">
                    {correspondenciasFiltradas.map((item) => {
                        const isRecibido = item.estado === 'recibido';
                        const isNotificado = item.estado === 'notificado';
                        const isEntregado = item.estado === 'entregado';
                        const isCancelado = item.estado === 'cancelado';

                        // Progreso: 1=Recibido, 2=Notificado, 3=Entregado
                        const pasoActual = isEntregado ? 3 : isNotificado ? 2 : isRecibido ? 1 : 0;

                        return (
                            <div
                                key={item.id}
                                className="bg-white rounded-2xl border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transition-all overflow-hidden"
                            >
                                {/* Header de la tarjeta */}
                                <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-slate-50/90 to-white flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3.5">
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#61257b] to-[#8b3fa8] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                                            <Package className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-mono text-sm font-black text-gray-900 bg-gray-100 px-2.5 py-0.5 rounded-lg border border-gray-200">
                                                    {item.codigo || `#${item.id}`}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => copiarAlPortapapeles(item.codigo || String(item.id), item.id, 'codigo')}
                                                    className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-gray-700 transition"
                                                    title="Copiar código"
                                                >
                                                    {copiadoId === item.id ? (
                                                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                    ) : (
                                                        <Copy className="w-3.5 h-3.5" />
                                                    )}
                                                </button>

                                                {getEstadoCorrespondenciaBadge(item.estado)}

                                                {item.afiliacion_empresa && (
                                                    <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                                                        <Building className="w-3 h-3" />
                                                        {item.afiliacion_empresa}
                                                    </span>
                                                )}
                                                {item.cita_codigo && (
                                                    <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                                        Cita: {item.cita_codigo}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                                                <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                                                Recibido el: <strong className="text-gray-700 font-semibold">{formatDateTime(item.fecha_recepcion)}</strong>
                                                {item.usuario_recibe_nombre && ` • Recepcionista: ${item.usuario_recibe_nombre}`}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Acciones Rápidas */}
                                    <div className="flex flex-wrap items-center gap-2">
                                        {/* Botón Notificar Llegada (si está recibido) */}
                                        {isRecibido && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCorrespondenciaSeleccionadaObj(item);
                                                    setModalNotificarLlegadaOpen(true);
                                                }}
                                                className="px-3 py-1.5 text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl transition-all flex items-center gap-1.5 shadow-sm"
                                            >
                                                <Bell className="w-3.5 h-3.5" />
                                                Notificar Llegada
                                            </button>
                                        )}

                                        {/* Botón Validar Entrega (si está recibido o notificado) */}
                                        {(isRecibido || isNotificado) && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCorrespondenciaSeleccionadaObj(item);
                                                    setModalValidarEntregaOpen(true);
                                                }}
                                                className="px-3.5 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-95 rounded-xl transition-all flex items-center gap-1.5 shadow-sm"
                                            >
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                Entregar Paquete
                                            </button>
                                        )}

                                        {/* Botón WhatsApp */}
                                        <button
                                            type="button"
                                            onClick={() => enviarNotificacionWhatsAppCliente(item)}
                                            className="px-3 py-1.5 text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-all flex items-center gap-1.5"
                                            title="Enviar aviso por WhatsApp"
                                        >
                                            <Send className="w-3.5 h-3.5 text-emerald-600" />
                                            WhatsApp
                                        </button>

                                        {/* Botón Editar */}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setCorrespondenciaSeleccionadaObj(item);
                                                setModalEditarCorrespondenciaOpen(true);
                                            }}
                                            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all flex items-center gap-1.5"
                                        >
                                            <Eye className="w-3.5 h-3.5 text-gray-500" />
                                            Editar / Ver
                                        </button>
                                    </div>
                                </div>

                                {/* PIPELINE / PROCESO DE LA CORRESPONDENCIA (3 PASOS) */}
                                <div className="p-5 bg-gradient-to-b from-gray-50/50 to-white border-b border-gray-100">
                                    <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                        <Activity className="w-3.5 h-3.5 text-[#61257b]" />
                                        Seguimiento del Proceso de Correspondencia
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 relative">
                                        {/* Paso 1: Recepción */}
                                        <div className={`p-3.5 rounded-xl border transition-all ${pasoActual >= 1
                                                ? 'bg-blue-50/60 border-blue-200'
                                                : 'bg-gray-50 border-gray-200 opacity-60'
                                            }`}>
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                                                    1
                                                </div>
                                                <span className="text-xs font-bold text-blue-900">Recepción Física</span>
                                                <CheckCircle2 className="w-4 h-4 text-blue-600 ml-auto" />
                                            </div>
                                            <p className="text-[11px] text-gray-600">
                                                {formatDateTime(item.fecha_recepcion)}
                                            </p>
                                            <p className="text-[11px] text-gray-500 truncate mt-0.5">
                                                Recibió: {item.usuario_recibe_nombre || 'Personal Recepción'}
                                            </p>
                                        </div>

                                        {/* Paso 2: Notificación */}
                                        <div className={`p-3.5 rounded-xl border transition-all ${isNotificado || isEntregado
                                                ? 'bg-amber-50/70 border-amber-200'
                                                : isRecibido
                                                    ? 'bg-white border-blue-300 ring-2 ring-blue-100'
                                                    : 'bg-gray-50 border-gray-200 opacity-60'
                                            }`}>
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isNotificado || isEntregado
                                                        ? 'bg-amber-600 text-white'
                                                        : 'bg-gray-200 text-gray-700'
                                                    }`}>
                                                    2
                                                </div>
                                                <span className="text-xs font-bold text-gray-900">Notificación al Cliente</span>
                                                {isNotificado || isEntregado ? (
                                                    <CheckCircle2 className="w-4 h-4 text-amber-600 ml-auto" />
                                                ) : (
                                                    <span className="text-[10px] font-bold text-amber-600 ml-auto">Pendiente</span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-gray-600">
                                                PIN: <strong className="font-mono text-gray-900">{item.pin_seguridad || 'Sin PIN'}</strong>
                                            </p>
                                            <p className="text-[11px] text-gray-500 truncate mt-0.5">
                                                {isNotificado ? 'Cliente notificado para retiro' : isEntregado ? 'Notificado previamente' : 'Listo para notificar'}
                                            </p>
                                        </div>

                                        {/* Paso 3: Entrega */}
                                        <div className={`p-3.5 rounded-xl border transition-all ${isEntregado
                                                ? 'bg-emerald-50/70 border-emerald-200'
                                                : 'bg-gray-50 border-gray-200 opacity-70'
                                            }`}>
                                            <div className="flex items-center gap-2 mb-1.5">
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${isEntregado ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'
                                                    }`}>
                                                    3
                                                </div>
                                                <span className="text-xs font-bold text-gray-900">Retiro / Entrega</span>
                                                {isEntregado ? (
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 ml-auto" />
                                                ) : (
                                                    <span className="text-[10px] font-medium text-gray-500 ml-auto">Pendiente</span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-gray-600">
                                                {item.fecha_entrega ? formatDateTime(item.fecha_entrega) : 'En custodia en recepción'}
                                            </p>
                                            <p className="text-[11px] text-gray-500 truncate mt-0.5">
                                                {item.receptor_nombre ? `Receptor: ${item.receptor_nombre}` : 'Esperando retiro'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Cuerpo Detallado con Información del Paquete y Fotos */}
                                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6 text-xs">
                                    {/* Columna 1: Remitente */}
                                    <div className="space-y-1.5">
                                        <span className="font-bold text-gray-400 uppercase tracking-wider text-[10px] block mb-1">
                                            👤 Remitente / Empresa de Encomienda
                                        </span>
                                        <p className="font-bold text-gray-900 text-sm">
                                            {item.remitente_nombre} {item.remitente_apellido || ''}
                                        </p>
                                        <p className="text-gray-600 flex items-center gap-1">
                                            <Phone className="w-3 h-3 text-gray-400" />
                                            {item.remitente_telefono || 'Sin teléfono registrado'}
                                        </p>
                                        <p className="text-gray-500">
                                            CI/NIT: <strong className="text-gray-700">{item.remitente_ci_nit || 'N/A'}</strong>
                                        </p>
                                    </div>

                                    {/* Columna 2: Destinatario & Descripción */}
                                    <div className="space-y-1.5">
                                        <span className="font-bold text-gray-400 uppercase tracking-wider text-[10px] block mb-1">
                                            🏢 Destinatario Asignado
                                        </span>
                                        <p className="font-bold text-gray-900 text-sm">
                                            {item.destinatario_nombre || (usuario ? `${usuario.first_name} ${usuario.last_name}` : 'Cliente')}
                                        </p>
                                        <p className="text-gray-600 flex items-center gap-1">
                                            <Mail className="w-3 h-3 text-gray-400" />
                                            {item.destinatario_email || usuario?.email || 'N/A'}
                                        </p>
                                        <div className="mt-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                            <span className="text-[10px] text-gray-400 font-bold block mb-0.5">Descripción del contenido:</span>
                                            <p className="text-gray-700 font-medium italic">
                                                {item.descripcion || 'Sin descripción detallada registrada.'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Columna 3: Seguridad & Fotos de Respaldo */}
                                    <div className="space-y-2">
                                        <span className="font-bold text-gray-400 uppercase tracking-wider text-[10px] block mb-1">
                                            🔐 Credenciales y Fotografías
                                        </span>

                                        {/* PIN de seguridad */}
                                        <div className="flex items-center justify-between p-2 rounded-xl bg-slate-900 text-white">
                                            <div className="flex items-center gap-2">
                                                <KeyRound className="w-3.5 h-3.5 text-amber-400" />
                                                <span className="text-[11px] text-gray-300">PIN de Entrega:</span>
                                                <strong className="font-mono text-sm tracking-widest text-amber-300">
                                                    {item.pin_seguridad || '----'}
                                                </strong>
                                            </div>
                                            {item.pin_seguridad && (
                                                <button
                                                    type="button"
                                                    onClick={() => copiarAlPortapapeles(item.pin_seguridad || '', item.id, 'pin')}
                                                    className="p-1 hover:bg-white/10 rounded text-gray-300 hover:text-white transition"
                                                    title="Copiar PIN"
                                                >
                                                    {copiadoPinId === item.id ? (
                                                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                                                    ) : (
                                                        <Copy className="w-3.5 h-3.5" />
                                                    )}
                                                </button>
                                            )}
                                        </div>

                                        {/* Link de seguimiento público */}
                                        {item.token_verificacion && (
                                            <div className="flex items-center justify-between p-2 rounded-xl bg-blue-50 border border-blue-200">
                                                <span className="text-[11px] font-semibold text-blue-800 flex items-center gap-1 truncate">
                                                    <ExternalLink className="w-3 h-3 text-blue-600" />
                                                    Seguimiento público
                                                </span>
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const url = `${window.location.origin}/correspondencias/seguimiento-publico/${item.token_verificacion}`;
                                                            navigator.clipboard.writeText(url);
                                                            alert('Enlace de seguimiento copiado al portapapeles');
                                                        }}
                                                        className="px-2 py-0.5 text-[10px] font-bold bg-white text-blue-700 hover:bg-blue-100 rounded border border-blue-200 transition"
                                                    >
                                                        Copiar Link
                                                    </button>
                                                    <a
                                                        href={`/correspondencias/seguimiento-publico/${item.token_verificacion}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="p-1 text-[#c1348b] hover:text-[#8f1f64]"
                                                        title="Abrir enlace de seguimiento"
                                                    >
                                                        <ArrowUpRight className="w-3.5 h-3.5" />
                                                    </a>
                                                </div>
                                            </div>
                                        )}

                                        {/* Fotos adjuntas */}
                                        <div className="flex items-center gap-2 pt-1">
                                            {item.imagen && (
                                                <button
                                                    type="button"
                                                    onClick={() => setVerFotoModalUrl(item.imagen || null)}
                                                    className="flex-1 p-2 rounded-xl border border-gray-200 hover:border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700 flex items-center justify-center gap-1.5 transition text-[11px] font-semibold"
                                                >
                                                    <Camera className="w-3.5 h-3.5 text-[#61257b]" />
                                                    Foto Paquete
                                                </button>
                                            )}
                                            {item.imagen_entrega && (
                                                <button
                                                    type="button"
                                                    onClick={() => setVerFotoModalUrl(item.imagen_entrega || null)}
                                                    className="flex-1 p-2 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 flex items-center justify-center gap-1.5 transition text-[11px] font-semibold"
                                                >
                                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                                    Foto Entrega
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-200 shadow-sm">
                    <div className="w-16 h-16 bg-blue-50 text-[#61257b] rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <Package className="w-8 h-8" />
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">
                        {busquedaCorrespondencia || filtroEstadoCorrespondencia !== 'todos'
                            ? 'No se encontraron correspondencias con los filtros aplicados'
                            : 'No hay correspondencias registradas para este cliente'}
                    </h4>
                    <p className="text-xs text-gray-500 max-w-md mx-auto mb-5">
                        {busquedaCorrespondencia || filtroEstadoCorrespondencia !== 'todos'
                            ? 'Intenta restablecer los filtros o la búsqueda para ver más resultados.'
                            : 'Puedes registrar la primera encomienda, carta o paquete recibido en recepción para este usuario.'}
                    </p>
                    <button
                        type="button"
                        onClick={() => setModalNuevaCorrespondenciaOpen(true)}
                        className="px-5 py-2.5 text-xs font-bold text-white bg-gradient-to-r from-[#c1348b] to-[#8f1f64] hover:opacity-95 rounded-xl transition shadow"
                    >
                        + Registrar Nueva Correspondencia
                    </button>
                </div>
            )}
        </div>
    );

    const renderContenidoTrazabilidad = () => (
        <div className="kd-section overflow-hidden">
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Trazabilidad del Cliente</h3>
                        <p className="text-sm text-gray-600 mt-1">
                            Historial de visitas a sucursales y actividades
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar por sucursal, código..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#61257b]/20 focus:border-[#61257b] text-sm w-full sm:w-64"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    <div className="kd-metric">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Última Sucursal Visitada</p>
                                <h3 className="text-xl font-bold text-gray-800 truncate">
                                    {statsSucursales.ultimaSucursal ? statsSucursales.ultimaSucursal.nombre : 'N/A'}
                                </h3>
                                {statsSucursales.ultimaSucursal && (
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-600">
                                            {statsSucursales.ultimaSucursal.fecha}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Cita: {statsSucursales.ultimaSucursal.citaCodigo}
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="p-2.5 rounded-lg bg-gradient-to-r from-[#c1348b]/10 to-[#8f1f64]/10">
                                <Building className="h-5 w-5 text-[#c1348b]" />
                            </div>
                        </div>
                    </div>

                    <div className="kd-metric">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Total Sucursales Diferentes</p>
                                <h3 className="text-2xl font-bold text-gray-800">{statsSucursales.totalSucursalesDiferentes}</h3>
                                <div className="mt-2">
                                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700">
                                        Sucursales visitadas
                                    </span>
                                </div>
                            </div>
                            <div className="p-2.5 rounded-lg bg-gradient-to-r from-[#ffd97c]/10 to-[#ea8406]/10">
                                <Target className="h-5 w-5 text-[#ea8406]" />
                            </div>
                        </div>
                    </div>

                    <div className="kd-metric">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 mb-1">Total Visitas Registradas</p>
                                <h3 className="text-2xl font-bold text-gray-800">{statsSucursales.historialCompleto.length}</h3>
                                <div className="mt-2">
                                    <span className="px-2 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700">
                                        Citas en todas las sucursales
                                    </span>
                                </div>
                            </div>
                            <div className="p-2.5 rounded-lg bg-gradient-to-r from-[#8b3fa8]/10 to-[#61257b]/10">
                                <Calendar className="h-5 w-5 text-[#61257b]" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mb-8">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Sucursales Visitadas</h4>
                    <div className="overflow-x-auto">
                        <AdminTable className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Sucursal
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Visitas
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Última Visita
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Detalle
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {statsSucursales.sucursalesVisitadas.map((sucursal) => (
                                    <tr key={sucursal.sucursalId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gradient-to-r from-[#61257b]/10 to-[#8b3fa8]/10 flex items-center justify-center">
                                                    <MapPin className="h-5 w-5 text-[#61257b]" />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{sucursal.nombre}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 font-semibold">{sucursal.visitas}</div>
                                            <div className="text-sm text-gray-500">visitas</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {sucursal.ultimaVisita}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => abrirModalSucursalCitas(sucursal)}
                                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#61257b] to-[#8b3fa8] hover:shadow-md rounded-lg transition-all"
                                            >
                                                <ExternalLink className="mr-1.5 h-4 w-4" />
                                                Ver {sucursal.citas.length} citas
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </AdminTable>
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-800">Historial Completo de Visitas</h4>
                        <span className="text-sm text-gray-500">
                            {statsSucursales.historialCompleto.length} registros
                        </span>
                    </div>
                    <div className="space-y-3">
                        <div className="grid gap-3">
                            {statsSucursales.historialCompleto.map((item) => (
                                <div
                                    key={item.citaId}
                                    className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-all"
                                >
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="text-sm font-mono text-gray-600 bg-white px-2 py-1 rounded border">
                                                    {item.codigo}
                                                </span>
                                                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-[#61257b]/10 to-[#8b3fa8]/10 text-[#61257b]">
                                                    {item.sucursal}
                                                </span>
                                                {getEstadoBadge(item.estado)}
                                            </div>

                                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                                <div>
                                                    <p className="text-xs text-gray-500">Servicio</p>
                                                    <p className="text-sm font-medium text-gray-800 truncate">
                                                        {item.servicio}
                                                    </p>
                                                </div>

                                                <div>
                                                    <p className="text-xs text-gray-500">Fecha</p>
                                                    <p className="text-sm font-medium text-gray-800">
                                                        {item.fecha}
                                                    </p>
                                                </div>

                                                <div>
                                                    <p className="text-xs text-gray-500">Hora</p>
                                                    <p className="text-sm font-medium text-gray-800">
                                                        {item.hora}
                                                    </p>
                                                </div>

                                                {citas.some(c => c.id === item.citaId && c.cliente === usuario?.id) && <div>
                                                    <p className="text-xs text-gray-500">Monto</p>
                                                    <p className="text-sm font-medium text-gray-800">
                                                        Bs {item.monto.toFixed(2)}
                                                    </p>
                                                </div>}

                                                <div>
                                                    <p className="text-xs text-gray-500">Acciones</p>
                                                    {citas.some(c => c.id === item.citaId && c.cliente === usuario?.id) && <button
                                                        onClick={() => handleCitaClick(item.citaId)}
                                                        className="mt-1 text-sm text-[#61257b] hover:text-[#0056a3] font-medium flex items-center gap-1"
                                                    >
                                                        Ver detalles
                                                        <ExternalLink className="h-3 w-3" />
                                                    </button>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderContenidoDomiciliacion = () => {
        if (!afiliacionCliente) {
            return (
                <div className="bg-white rounded-3xl p-10 border border-gray-200 text-center shadow-sm max-w-2xl mx-auto my-8 space-y-4">
                    <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-inner">
                        <Building2 className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-black text-gray-900">
                        Sin Expediente de Domiciliación Activo
                    </h3>
                    <p className="text-sm text-gray-500 max-w-md mx-auto leading-relaxed">
                        Este cliente aún no cuenta con una membresía de Domiciliación (Oficina Virtual / Dirección Fiscal SIN & SEPREC). El formulario se habilita al completar la compra y el pago en POS.
                    </p>
                    <div className="pt-2">
                        <p className="text-sm text-[#61257b]">Adquiere el servicio de oficina virtual en POS para habilitar el expediente.</p>
                    </div>
                </div>
            );
        }

        const montoTotal = Number(afiliacionCliente.monto_total || 0);
        const montoPagado = Number(afiliacionCliente.monto_pagado || 0);
        const saldoPendiente = Math.max(0, montoTotal - montoPagado);
        const estadoPago = afiliacionCliente.estado_pago || (montoPagado >= montoTotal ? 'pagado' : montoPagado > 0 ? 'parcial' : 'pendiente');
        const pagosRegistrados = Array.isArray(afiliacionCliente.pagos) ? afiliacionCliente.pagos : [];

        return (
            <div className="space-y-6">
                {/* 1. Resumen de la Empresa Domiciliada */}
                <div className="kd-office">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-[#61257b] to-[#8b3fa8] flex items-center justify-center shadow-lg">
                                <Building2 className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#61257b]">
                                        Expediente de oficina virtual
                                    </span>
                                    <span className={`px-2.5 py-0.5 text-[10px] font-black rounded-full uppercase ${afiliacionCliente.estado === 'activo'
                                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                            : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                        }`}>
                                        {afiliacionCliente.estado === 'activo' ? 'Activo / Domiciliado' : 'En proceso'}
                                    </span>
                                </div>
                                <h2 className="text-2xl font-black mt-1">
                                    {afiliacionCliente.empresa_nombre}
                                </h2>
                                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mt-1.5">
                                    <span>NIT: <strong>{afiliacionCliente.nit || 'Pendiente'}</strong></span>
                                    <span>•</span>
                                    <span>Rep. Legal: <strong>{afiliacionCliente.representante_legal || usuario?.first_name}</strong></span>
                                    <span>•</span>
                                    <span>Vigencia: <strong>{afiliacionCliente.periodo_inicio} a {afiliacionCliente.periodo_fin}</strong></span>
                                    <span>•</span>
                                    <span>Horas de regalo del período vigente</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <div className="kd-contact text-center">
                                <span className="text-[10px] text-gray-400 font-bold uppercase block">Formulario de registro</span>
                                <span className="text-base font-bold text-gray-900">
                                    {afiliacionCliente.pasos_completados_count}/7 Pasos ({afiliacionCliente.porcentaje_progreso}%)
                                </span>
                            </div>
                            <button
                                onClick={() => setModalGestionAfiliacionOpen(true)}
                                className="px-5 py-2.5 bg-[#61257b] hover:bg-[#8f1f64] text-white text-xs font-bold rounded-2xl shadow transition flex items-center gap-2"
                            >
                                <span>Gestionar Flujo (7 Pasos)</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. PLAN DE PAGOS DE DOMICILIACIÓN */}
                <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-sm space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                Módulo Financiero
                            </span>
                            <h3 className="text-lg font-black text-gray-900 flex items-center gap-2">
                                <Banknote className="w-5 h-5 text-[#61257b]" />
                                <span>Plan de Pagos de Domiciliación</span>
                            </h3>
                            <p className="text-xs text-gray-500">
                                Gestión de cuotas, cobranzas y comprobantes de caja para esta oficina virtual
                            </p>
                        </div>

                        <p className="text-sm text-[#61257b]">Adquiere el servicio de oficina virtual en POS para habilitar el expediente.</p>
                    </div>

                    {/* Stat Cards Financieras */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                            <span className="text-[10px] font-bold text-gray-400 uppercase">Tarifa Plan</span>
                            <p className="text-xl font-black text-gray-900 mt-1">
                                {montoTotal.toFixed(2)} <span className="text-xs font-normal text-gray-500">Bs</span>
                            </p>
                            <span className="text-[11px] text-gray-500 mt-0.5 block">
                                Importe de la compra en POS
                            </span>
                        </div>

                        <div className="bg-emerald-50/60 p-4 rounded-2xl border border-emerald-100">
                            <span className="text-[10px] font-bold text-emerald-800 uppercase">Total Pagado</span>
                            <p className="text-xl font-black text-emerald-700 mt-1">
                                {montoPagado.toFixed(2)} <span className="text-xs font-normal text-emerald-600">Bs</span>
                            </p>
                            <span className="text-[11px] text-emerald-600 mt-0.5 block">
                                {pagosRegistrados.length} {pagosRegistrados.length === 1 ? 'pago registrado' : 'pagos registrados'}
                            </span>
                        </div>

                        <div className="bg-rose-50/60 p-4 rounded-2xl border border-rose-100">
                            <span className="text-[10px] font-bold text-rose-800 uppercase">Saldo Pendiente</span>
                            <p className="text-xl font-black text-[#c1348b] mt-1">
                                {saldoPendiente.toFixed(2)} <span className="text-xs font-normal text-rose-600">Bs</span>
                            </p>
                            <span className="text-[11px] text-rose-600 mt-0.5 block">
                                {saldoPendiente > 0 ? 'Cobranza pendiente' : 'Al día'}
                            </span>
                        </div>

                        <div className="bg-blue-50/60 p-4 rounded-2xl border border-blue-100">
                            <span className="text-[10px] font-bold text-blue-800 uppercase">Estado de Pago</span>
                            <div className="mt-1.5">
                                <span className={`px-3 py-1 text-xs font-black rounded-full uppercase ${estadoPago === 'pagado'
                                        ? 'bg-emerald-100 text-emerald-800'
                                        : estadoPago === 'parcial'
                                            ? 'bg-amber-100 text-amber-800'
                                            : 'bg-rose-100 text-rose-800'
                                    }`}>
                                    {estadoPago === 'pagado' ? 'Pagado Total' : estadoPago === 'parcial' ? 'Pago Parcial' : 'Pendiente'}
                                </span>
                            </div>
                            <span className="text-[11px] text-blue-700 mt-1.5 block truncate">
                                {afiliacionCliente.servicio_nombre || 'Plan Domicilio Comercial'}
                            </span>
                        </div>
                    </div>

                    {/* Tabla de Pagos Realizados */}
                    <div className="space-y-3 pt-2">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700">
                            Historial de Pagos Recibidos
                        </h4>
                        {pagosRegistrados.length === 0 ? (
                            <div className="p-8 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                <p className="text-xs text-gray-500 font-medium">
                                    No se han registrado pagos para esta domiciliación aún. Presiona "+ Registrar Pago de Domiciliación" para registrar el primer cobro en caja.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
                                <AdminTable className="w-full text-xs text-left">
                                    <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-[10px]">
                                        <tr>
                                            <th className="py-3 px-4">Código</th>
                                            <th className="py-3 px-4">Fecha</th>
                                            <th className="py-3 px-4">Método de Pago</th>
                                            <th className="py-3 px-4 text-right">Monto</th>
                                            <th className="py-3 px-4 text-right">Recibido</th>
                                            <th className="py-3 px-4 text-right">Cambio</th>
                                            <th className="py-3 px-4 text-center">Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {pagosRegistrados.map((p: any) => (
                                            <tr key={p.id} className="hover:bg-gray-50/80 transition">
                                                <td className="py-3 px-4 font-mono font-bold text-[#61257b]">
                                                    {p.codigo || `#${p.id}`}
                                                </td>
                                                <td className="py-3 px-4 text-gray-600">
                                                    {new Date(p.fecha_pago).toLocaleDateString()} {new Date(p.fecha_pago).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </td>
                                                <td className="py-3 px-4 font-semibold text-gray-800">
                                                    {p.tipo_pago_nombre || 'Pago Directo'}
                                                </td>
                                                <td className="py-3 px-4 text-right font-black text-gray-900">
                                                    {Number(p.monto).toFixed(2)} Bs
                                                </td>
                                                <td className="py-3 px-4 text-right text-gray-600">
                                                    {p.efectivo_recibido ? `${Number(p.efectivo_recibido).toFixed(2)} Bs` : '-'}
                                                </td>
                                                <td className="py-3 px-4 text-right text-emerald-600 font-semibold">
                                                    {p.cambio ? `${Number(p.cambio).toFixed(2)} Bs` : '0.00 Bs'}
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded-full text-[10px]">
                                                        Registrado
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </AdminTable>
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. BENEFICIOS CONFIGURADOS: HORAS DE REGALO Y CORRESPONDENCIA */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Horas de Regalo Config */}
                    <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-sm space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                                    <Gift className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-black text-gray-900">Bolsón de Horas de Cortesía</h4>
                                    <p className="text-[11px] text-gray-500">Sincronizado con HorasRegaloConfig</p>
                                </div>
                            </div>
                            <span className="px-2.5 py-1 bg-purple-100 text-purple-800 rounded-full font-bold text-[10px]">
                                {afiliacionCliente.horas_gratuitas_asignadas || 0} hrs / mes
                            </span>
                        </div>

                        <div className="bg-purple-50/60 p-4 rounded-2xl border border-purple-100 flex items-center justify-between">
                            <div>
                                <span className="text-[10px] uppercase font-bold text-purple-900 block">Saldo Disponible</span>
                                <span className="text-2xl font-black text-purple-950">
                                    {afiliacionCliente.horas_gratuitas_disponibles} hrs
                                </span>
                                <span className="text-[11px] text-purple-700 block mt-0.5">
                                    ({afiliacionCliente.minutos_disponibles} minutos restantes)
                                </span>
                            </div>
                            <div className="text-right text-[11px] text-purple-800 space-y-1">
                                <div>Consumidas: <strong>{afiliacionCliente.horas_gratuitas_consumidas || 0} hrs</strong></div>
                                <div>No acumulable entre meses</div>
                            </div>
                        </div>

                        {afiliacionCliente.config_horas_regalo_info && (
                            <p className="text-[11px] text-gray-500 bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                                <strong>Configuración activa:</strong> {afiliacionCliente.config_horas_regalo_info.horas_asignadas} horas vinculadas al servicio <em>"{afiliacionCliente.config_horas_regalo_info.servicio_nombre}"</em>.
                            </p>
                        )}
                    </div>

                    {/* Correspondencia Config */}
                    <div className="bg-white rounded-3xl p-6 border border-gray-200 shadow-sm space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="w-9 h-9 rounded-xl bg-blue-50 text-[#61257b] flex items-center justify-center">
                                    <Package className="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-black text-gray-900">Servicio de Correspondencia</h4>
                                    <p className="text-[11px] text-gray-500">Sincronizado con CorrespondenciaConfig</p>
                                </div>
                            </div>
                            <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full font-bold text-[10px]">
                                Habilitada
                            </span>
                        </div>

                        <div className="bg-blue-50/60 p-4 rounded-2xl border border-blue-100 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-blue-950">Recepción Física & Notificaciones</span>
                                <span className="text-xs font-black text-[#61257b]">
                                    {afiliacionCliente.correspondencias_count || 0} recibidas
                                </span>
                            </div>
                            <p className="text-xs text-blue-900">
                                {afiliacionCliente.config_correspondencia_info?.descripcion || 'Recepción de correspondencia, paquetería fiscal y comercial con registro digital.'}
                            </p>
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={() => setTabPrincipalActiva('correspondencia')}
                        aria-current={tabPrincipalActiva === 'correspondencia' ? 'page' : undefined}
                                className="text-xs font-bold text-[#61257b] hover:underline flex items-center gap-1"
                            >
                                <span>Ver paquetes en Historial de Correspondencia</span>
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-[#F8FAFC] to-gray-100">
                <div className="text-center">
                    <div className="relative">
                        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#c1348b] mx-auto"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-[#c1348b]/20 to-[#8f1f64]/20"></div>
                        </div>
                    </div>
                    <p className="mt-4 text-gray-600 font-medium">Cargando datos del usuario...</p>
                </div>
            </div>
        );
    }

    if (!usuario) {
        return (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md mx-auto mt-20 border border-gray-200">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#c1348b]/10 to-[#8f1f64]/10 flex items-center justify-center">
                    <User className="h-10 w-10 text-[#c1348b]" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Usuario no encontrado</h3>
                <p className="text-gray-600 mb-8">El usuario solicitado no existe en el sistema</p>
                <Link
                    to="/kardex-citas"
                    className="inline-flex items-center px-6 py-3 rounded-lg bg-gradient-to-r from-[#61257b] to-[#8b3fa8] text-white font-semibold hover:shadow-lg transition-all duration-300"
                >
                    <ArrowLeft className="mr-2 h-5 w-5" />
                    Volver al listado
                </Link>
            </div>
        );
    }


    const isDeclaracionFirmada = Boolean(clienteObj?.declaracion_jurada_firmada ?? (usuario as any)?.declaracion_jurada_firmada);
    const fotoDeclaracionUrl = clienteObj?.foto_declaracion_jurada_url ?? (usuario as any)?.foto_declaracion_jurada_url;
    const fechaDeclaracion = clienteObj?.fecha_declaracion_jurada ?? (usuario as any)?.fecha_declaracion_jurada;

    const handleGuardarDeclaracion = async () => {
        if (!clienteObj || !archivoDeclaracion) return;
        setGuardandoDeclaracion(true);
        try {
            const updated = await updateCliente(clienteObj.id, {
                foto_declaracion_jurada: archivoDeclaracion,
                declaracion_jurada_firmada: true
            });
            setClienteObj(updated);
            if (usuarioId) {
                const refreshedUser = await fetchUsuarioById(parseInt(usuarioId));
                setUsuario(refreshedUser);
            }
            setModalSubirDeclaracionOpen(false);
            setArchivoDeclaracion(null);
        } catch (err) {
            /* Browser diagnostics omitted. */
        } finally {
            setGuardandoDeclaracion(false);
        }
    };

    return (

        <div className="kardex-page">
          <div className="kardex-content space-y-6">
            <div className="kd-panel relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-[#c1348b]/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />

                <div className="relative z-10">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                        <div className="flex items-center gap-4 min-w-0">
                            <div className="relative">
                                <div className="h-20 w-20 shrink-0 rounded-2xl bg-[#c1348b]/10 border border-[#c1348b]/20 flex items-center justify-center overflow-hidden">
                                    {clienteObj?.foto_url ? (
                                        <img src={clienteObj.foto_url} alt={usuario.first_name} className="h-full w-full object-cover" />
                                    ) : (
                                        <User className="h-10 w-10 text-[#c1348b]" />
                                    )}
                                </div>
                            </div>
                            <div>
                                <span className="kd-eyebrow">Kardex del cliente</span>
                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                    <h1 className="kd-heading">
                                        {usuario.first_name} {usuario.last_name}
                                    </h1>
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#61257b]/10 text-[#61257b]">
                                        {usuario.tipo_nivel_usuario?.nombre || 'Cliente'}
                                    </span>
                                </div>
                                <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-gray-500">
                                    <span className="flex items-center gap-1.5">
                                        <CreditCard className="h-3.5 w-3.5" />
                                        ID Usuario: {usuario.id}
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Calendar className="h-3.5 w-3.5" />
                                        {stats.totalCitas} Reservas Totales
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <MapPin className="h-3.5 w-3.5" />
                                        {statsSucursales.totalSucursalesDiferentes} Sucursales Visitadas
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <Link
                                to="/kardex-citas"
                                className="kd-button"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Volver al Kardex
                            </Link>

                            <Link
                                to={`/dashboard/kardex-clientes/historial-pagos/${usuario.id}`}
                                className="kd-button kd-button-primary"
                            >
                                <FileText className="mr-2 h-4 w-4" />
                                Historial de Pagos
                            </Link>
                        </div>
                    </div>

                    {/* Cards de Información de Contacto y Declaración Jurada */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                        <div className="kd-contact">
                            <div className="flex items-center gap-2.5 mb-1.5">
                                <Mail className="h-4 w-4 text-gray-500" />
                                <span className="text-gray-600 text-xs font-bold uppercase tracking-wider">Email</span>
                            </div>
                            <p className="text-gray-900 font-medium text-sm" title={usuario.email || ''}>{usuario.email || 'No disponible'}</p>
                        </div>

                        <div className="kd-contact">
                            <div className="flex items-center gap-2.5 mb-1.5">
                                <Phone className="h-4 w-4 text-gray-500" />
                                <span className="text-gray-600 text-xs font-bold uppercase tracking-wider">Teléfono</span>
                            </div>
                            <p className="text-gray-900 font-medium text-sm">{usuario.telefono || 'No disponible'}</p>
                        </div>

                        {usuario.perfil_asociado === 'cliente' ? (
                            <div className="kd-contact">
                                <div className="flex items-center gap-2.5 mb-1.5">
                                    <CreditCard className="h-4 w-4 text-gray-500" />
                                    <span className="text-gray-600 text-xs font-bold uppercase tracking-wider">CI / NIT</span>
                                </div>
                                <p className="text-gray-900 font-medium text-sm">{usuario.ci_nit || 'No disponible'}</p>
                            </div>
                        ) : (
                            <div className="kd-contact">
                                <div className="flex items-center gap-2.5 mb-1.5">
                                    <Briefcase className="h-4 w-4 text-gray-500" />
                                    <span className="text-gray-600 text-xs font-bold uppercase tracking-wider">Cargo</span>
                                </div>
                                <p className="text-gray-900 font-medium text-sm">{usuario.cargo || 'No disponible'}</p>
                            </div>
                        )}

                        {/* Bloque Destacado de Declaración Jurada */}
                        {usuario.perfil_asociado === 'cliente' && (
                            <div className="kd-contact flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <div className="flex items-center gap-1.5 text-gray-900">
                                            <FileCheck className="h-4 w-4 text-emerald-600" />
                                            <span className="text-gray-600 text-xs font-bold uppercase tracking-wider">Dec. Jurada</span>
                                        </div>
                                        {isDeclaracionFirmada ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-400/20 text-emerald-700 border border-emerald-400/30">
                                                ✓ Acreditada
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-400/20 text-amber-700 border border-amber-400/30">
                                                ⚠ Pendiente
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 font-medium">
                                        {isDeclaracionFirmada && fechaDeclaracion
                                            ? `Firmada el ${new Date(fechaDeclaracion).toLocaleDateString('es-ES')}`
                                            : 'Firma pendiente de registro'}
                                    </p>
                                </div>

                                <div className="mt-3 flex items-center gap-2">
                                    {isDeclaracionFirmada && (
                                        <button
                                            type="button"
                                            onClick={() => setModalVerDeclaracionOpen(true)}
                                            className="flex-1 py-1.5 px-3 rounded-lg text-xs font-bold bg-white text-slate-900 hover:bg-white/90 transition-all flex items-center justify-center gap-1.5 shadow-sm"
                                        >
                                            <Eye className="h-3.5 w-3.5 text-[#61257b]" />
                                            Ver Firma
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setModalSubirDeclaracionOpen(true)}
                                        className={`py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${isDeclaracionFirmada
                                            ? 'bg-white text-[#8f1f64] border border-[#c1348b]/20'
                                            : 'w-full bg-gradient-to-r from-[#c1348b] to-[#8f1f64] text-white hover:opacity-95 shadow-sm'
                                            }`}
                                    >
                                        <Camera className="h-3.5 w-3.5" />
                                        {isDeclaracionFirmada ? 'Actualizar' : 'Tomar Foto a Firma'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tarjeta de Afiliación / Requisitos Fiscales (Miro) */}
            <div className="kd-office">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-[#c1348b] to-[#8f1f64] flex items-center justify-center shadow">
                            <Building className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-[#8f1f64]">
                                    Oficina virtual
                                </span>
                                {afiliacionCliente && (
                                    <span className={`px-2.5 py-0.5 text-[10px] font-bold rounded-full uppercase ${afiliacionCliente.estado === 'activo'
                                        ? 'bg-emerald-500/20 text-emerald-700 border border-emerald-500/30'
                                        : 'bg-amber-500/20 text-amber-700 border border-amber-500/30'
                                        }`}>
                                        {afiliacionCliente.estado === 'activo' ? 'Afiliado Activo' : 'En proceso'}
                                    </span>
                                )}
                            </div>
                            <h3 className="text-lg font-black mt-0.5">
                                {afiliacionCliente ? afiliacionCliente.empresa_nombre : 'Sin Empresa Afiliada Registrada'}
                            </h3>
                            <p className="text-xs text-gray-400">
                                {afiliacionCliente
                                    ? `NIT: ${afiliacionCliente.nit || 'Pendiente'} • Representante: ${afiliacionCliente.representante_legal || usuario.first_name}`
                                    : 'Apertura de expediente legal, fiscal (SIN/SEPREC), correspondencia y horas de regalo configuradas.'}
                            </p>
                        </div>
                    </div>

                    {afiliacionCliente ? (
                        <div className="flex flex-wrap items-center gap-4 w-full md:w-auto">
                            {/* Progreso 7 Pasos */}
                            <div className="kd-contact text-center">
                                <div className="text-[10px] text-gray-400 uppercase font-bold">Formulario de registro</div>
                                <div className="text-sm font-bold text-gray-900">
                                    {afiliacionCliente.pasos_completados_count}/7 Pasos ({afiliacionCliente.porcentaje_progreso}%)
                                </div>
                            </div>

                            {/* Horas Gratuitas */}
                            <div className="bg-purple-500/20 border border-purple-500/30 px-4 py-2.5 rounded-xl text-center">
                                <div className="text-[10px] text-[#61257b] uppercase font-bold">Horas Gratuitas</div>
                                <div className="text-sm font-black text-[#61257b]">
                                    {afiliacionCliente.horas_gratuitas_disponibles} hrs disponibles
                                </div>
                            </div>

                            <button
                                onClick={() => setTabPrincipalActiva('domiciliacion')}
                        aria-current={tabPrincipalActiva === 'domiciliacion' ? 'page' : undefined}
                                className="kd-button"
                            >
                                <Building2 className="w-3.5 h-3.5 text-[#61257b]" />
                                <span>Ver expediente y pagos</span>
                            </button>
                            <button
                                onClick={() => setModalGestionAfiliacionOpen(true)}
                                className="px-5 py-2.5 bg-[#61257b] hover:bg-[#8f1f64] text-white font-bold text-xs rounded-xl transition shadow"
                            >
                                Gestionar los 7 pasos
                            </button>
                        </div>
                    ) : (
                        <p className="text-sm text-[#61257b]">Adquiere el servicio de oficina virtual en POS para habilitar el expediente.</p>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="kd-metric">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 mb-1">Total Reservas</p>
                            <h3 className="text-2xl font-bold text-gray-800">{stats.totalCitas}</h3>
                            <div className="flex items-center gap-2 mt-2">
                                <div className="flex items-center gap-1">
                                    <Crown className="h-3.5 w-3.5 text-[#c1348b]" />
                                    <span className="text-xs text-gray-600">{stats.totalCitasTitular} titular</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Users className="h-3.5 w-3.5 text-[#61257b]" />
                                    <span className="text-xs text-gray-600">{stats.totalCitasAsociadas} asociado</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-2.5 rounded-lg bg-gradient-to-r from-[#c1348b]/10 to-[#8f1f64]/10">
                            <Activity className="h-5 w-5 text-[#c1348b]" />
                        </div>
                    </div>
                </div>

                <div className="kd-metric">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 mb-1">Estado Financiero</p>
                            <h3 className="text-2xl font-bold text-gray-800">
                                Bs {Number(stats.totalPagado).toFixed(2)}
                            </h3>
                            <div className="mt-2 flex flex-col gap-1">
                                <span className={`px-2 py-1 rounded-lg text-xs font-bold w-fit ${stats.deudaTotal > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                    {stats.deudaTotal > 0 ? `Deuda Total: Bs ${stats.deudaTotal.toFixed(2)}` : 'Al día'}
                                </span>
                                {stats.deudaAfiliacion > 0 && stats.deudaCitas > 0 && (
                                    <span className="text-[10px] text-gray-500 font-medium">
                                        (Reservas: Bs {stats.deudaCitas.toFixed(2)} · Domiciliación: Bs {stats.deudaAfiliacion.toFixed(2)})
                                    </span>
                                )}
                                {stats.deudaAfiliacion > 0 && stats.deudaCitas === 0 && (
                                    <span className="text-[10px] text-gray-500 font-medium">
                                        (Domiciliación pendiente: Bs {stats.deudaAfiliacion.toFixed(2)})
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="p-2.5 rounded-lg bg-gradient-to-r from-[#8b3fa8]/10 to-[#61257b]/10">
                            <DollarSign className="h-5 w-5 text-[#61257b]" />
                        </div>
                    </div>
                </div>

                <div className="kd-metric">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 mb-1">Servicio Más Usado</p>
                            <h3 className="text-lg font-bold text-gray-800 truncate">
                                {stats.servicioMasUsado || 'Sin datos'}
                            </h3>
                            {servicioMasUsadoData ? (
                                <div className="mt-1">
                                    <p className="text-xs text-gray-600">
                                        <span className="font-semibold text-[#c1348b]">{servicioMasUsadoData.cantidad}</span> usos
                                    </p>
                                </div>
                            ) : null}
                        </div>
                        <div className="p-2.5 rounded-lg bg-gradient-to-r from-[#ffd97c]/10 to-[#ea8406]/10">
                            <Target className="h-5 w-5 text-[#ea8406]" />
                        </div>
                    </div>
                </div>

                <div className="kd-metric">
                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 mb-1">Valor Total</p>
                            <h3 className="text-2xl font-bold text-gray-800">
                                Bs {Number(stats.totalCitasValor).toFixed(2)}
                            </h3>
                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                <span className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700">
                                    {((stats.totalPagado / (stats.totalCitasValor || 1)) * 100 || 0).toFixed(1)}% pagado
                                </span>
                                {stats.totalAfiliacionValor > 0 && (
                                    <span className="text-[10px] text-gray-400 font-medium">
                                        (inc. Domiciliación)
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="p-2.5 rounded-lg bg-gradient-to-r from-purple-500/10 to-purple-600/10">
                            <TrendingUp className="h-5 w-5 text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <nav className="kd-tabs" aria-label="Secciones del kardex">
                    <button
                        onClick={() => setTabPrincipalActiva('reservas')}
                        aria-current={tabPrincipalActiva === 'reservas' ? 'page' : undefined}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 ${tabPrincipalActiva === 'reservas'
                            ? 'border-[#c1348b] text-[#c1348b]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Historial de Reservas
                        </div>
                    </button>
                    <button
                        onClick={() => setTabPrincipalActiva('correspondencia')}
                        aria-current={tabPrincipalActiva === 'correspondencia' ? 'page' : undefined}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 ${tabPrincipalActiva === 'correspondencia'
                            ? 'border-[#61257b] text-[#61257b]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            Historial de Correspondencia
                            {(correspondencias.length > 0 || citasConCorrespondencia.length > 0) && (
                                <span className="bg-[#61257b] text-white text-xs rounded-full px-2 py-0.5 font-bold">
                                    {correspondencias.length || citasConCorrespondencia.length}
                                </span>
                            )}
                        </div>
                    </button>
                    <button
                        onClick={() => setTabPrincipalActiva('domiciliacion')}
                        aria-current={tabPrincipalActiva === 'domiciliacion' ? 'page' : undefined}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 ${tabPrincipalActiva === 'domiciliacion'
                            ? 'border-purple-600 text-purple-600 font-bold'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            Oficina virtual
                            {afiliacionCliente && (
                                <span className={`text-white text-xs rounded-full px-2 py-0.5 font-bold ${afiliacionCliente.estado === 'activo' ? 'bg-emerald-500' : 'bg-purple-600'}`}>
                                    {afiliacionCliente.estado === 'activo' ? 'Activo' : `${afiliacionCliente.pasos_completados_count}/7`}
                                </span>
                            )}
                        </div>
                    </button>
                    <button
                        onClick={() => setTabPrincipalActiva('trazabilidad')}
                        aria-current={tabPrincipalActiva === 'trazabilidad' ? 'page' : undefined}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-all duration-200 ${tabPrincipalActiva === 'trazabilidad'
                            ? 'border-[#8b3fa8] text-[#8b3fa8]'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Activity className="h-4 w-4" />
                            Trazabilidad
                            {statsSucursales.totalSucursalesDiferentes > 0 && (
                                <span className="bg-[#8b3fa8] text-white text-xs rounded-full px-2 py-0.5">
                                    {statsSucursales.totalSucursalesDiferentes}
                                </span>
                            )}
                        </div>
                    </button>
                </nav>
            </div>

            <div className="mt-6">
                {tabPrincipalActiva === 'reservas' ? renderContenidoReservas() :
                    tabPrincipalActiva === 'correspondencia' ? renderContenidoCorrespondencia() :
                        tabPrincipalActiva === 'domiciliacion' ? renderContenidoDomiciliacion() :
                            renderContenidoTrazabilidad()}
            </div>

          </div>
            {selectorSesionOpen && citaSeleccionada && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-900/55 p-3 sm:p-4" onMouseDown={e => { if (e.target === e.currentTarget) cerrarSelectorSesion(); }}>
                    <div
                        ref={selectorDialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="kardex-sesion-title"
                        aria-describedby="kardex-sesion-desc"
                        tabIndex={-1}
                        className="w-full max-w-md max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl outline-none sm:p-6"
                    >
                        <h3 id="kardex-sesion-title" className="text-base font-black text-slate-900">¿Desde qué caja cobras?</h3>
                        <p id="kardex-sesion-desc" className="mt-1 text-xs text-slate-500">
                            Tienes varias sesiones POS abiertas. El pago de la reserva {citaSeleccionada.codigo} se registrará en la caja que elijas.
                        </p>
                        <div className="mt-4 flex flex-col gap-2">
                            {sesionesAbiertas.map(s => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => elegirSesionParaPago(s.id)}
                                    className="flex min-h-[44px] flex-col items-start gap-1 rounded-xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700 hover:border-[#007FC8] hover:bg-[#007FC8]/5 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-amber-400 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <span className="min-w-0 break-words">{s.punto_de_venta_nombre || `Punto de venta #${s.punto_de_venta}`}{s.sucursal_nombre ? ` · ${s.sucursal_nombre}` : ''}</span>
                                    <span className="text-xs text-slate-400">{s.codigo || `#${s.id}`}</span>
                                </button>
                            ))}
                        </div>
                        <button type="button" onClick={cerrarSelectorSesion} className="mt-4 min-h-[44px] w-full rounded-xl border border-slate-200 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-amber-400">
                            Cancelar
                        </button>
                    </div>
                </div>
            )}
            {modalPagoOpen && citaSeleccionada && sesionPosPago !== null && (
                <PagosModal
                    isOpen={modalPagoOpen}
                    onClose={cerrarModalPago}
                    citaId={citaSeleccionada.id}
                    montoCita={Number(citaSeleccionada.total)}
                    montoPagado={Number(citaSeleccionada.monto_pagado)}
                    sesionPosId={sesionPosPago}
                    operadorToken={operador.token}
                    onIdentifyOperator={operador.openDialog}
                    onPagoSuccess={recargarPagos}
                />
            )}
            <OperadorPinDialog open={operador.dialogOpen} sesionId={sesionPosPago} onClose={operador.closeDialog} onGrant={operador.install} />
            {modalListadoPagosOpen && citaSeleccionada && (
                <PagosModalHistorial
                    isOpen={modalListadoPagosOpen}
                    onClose={() => setModalListadoPagosOpen(false)}
                    citaId={citaSeleccionada.id}
                />
            )}
            {modalHistorialCorrespondenciaOpen && citaSeleccionadaCorrespondencia && (
                <HistorialCorrespondenciaModal
                    isOpen={modalHistorialCorrespondenciaOpen}
                    onClose={() => setModalHistorialCorrespondenciaOpen(false)}
                    citaId={citaSeleccionadaCorrespondencia.id}
                    citaCodigo={citaSeleccionadaCorrespondencia.codigo}
                />
            )}
            {modalSucursalCitasOpen && sucursalSeleccionada && (
                <SucursalCitasModal
                    isOpen={modalSucursalCitasOpen}
                    onClose={() => setModalSucursalCitasOpen(false)}
                    sucursal={sucursalSeleccionada}
                />
            )}

            {/* Modal Nueva Correspondencia (con cliente preseleccionado) */}
            {modalNuevaCorrespondenciaOpen && (
                <NuevaCorrespondenciaModal
                    isOpen={modalNuevaCorrespondenciaOpen}
                    onClose={() => setModalNuevaCorrespondenciaOpen(false)}
                    onSave={() => recargarCorrespondencias()}
                    clienteInicial={usuario}
                />
            )}

            {/* Modal Notificar Llegada */}
            {modalNotificarLlegadaOpen && correspondenciaSeleccionadaObj && (
                <NotificarLlegadaModal
                    isOpen={modalNotificarLlegadaOpen}
                    correspondencia={correspondenciaSeleccionadaObj}
                    onClose={() => {
                        setModalNotificarLlegadaOpen(false);
                        setCorrespondenciaSeleccionadaObj(null);
                    }}
                    onSuccess={() => {
                        recargarCorrespondencias();
                        setModalNotificarLlegadaOpen(false);
                        setCorrespondenciaSeleccionadaObj(null);
                    }}
                />
            )}

            {/* Modal Validar Entrega */}
            {modalValidarEntregaOpen && correspondenciaSeleccionadaObj && (
                <ValidarEntregaModal
                    isOpen={modalValidarEntregaOpen}
                    correspondencia={correspondenciaSeleccionadaObj}
                    onClose={() => {
                        setModalValidarEntregaOpen(false);
                        setCorrespondenciaSeleccionadaObj(null);
                    }}
                    onSuccess={() => {
                        recargarCorrespondencias();
                        setModalValidarEntregaOpen(false);
                        setCorrespondenciaSeleccionadaObj(null);
                    }}
                />
            )}

            {/* Modal Editar Correspondencia */}
            {modalEditarCorrespondenciaOpen && correspondenciaSeleccionadaObj && (
                <EditarCorrespondenciaModal
                    isOpen={modalEditarCorrespondenciaOpen}
                    correspondencia={correspondenciaSeleccionadaObj}
                    onClose={() => {
                        setModalEditarCorrespondenciaOpen(false);
                        setCorrespondenciaSeleccionadaObj(null);
                    }}
                    onSave={() => {
                        recargarCorrespondencias();
                        setModalEditarCorrespondenciaOpen(false);
                        setCorrespondenciaSeleccionadaObj(null);
                    }}
                />
            )}

            {/* Lightbox / Modal Visualizador de Foto en Grande */}
            {verFotoModalUrl && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fadeIn"
                    onClick={() => setVerFotoModalUrl(null)}
                >
                    <div
                        className="bg-white rounded-2xl overflow-hidden max-w-2xl max-h-[90vh] shadow-2xl relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-4 bg-gray-900 text-white flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-300 flex items-center gap-1.5">
                                <Camera className="w-4 h-4 text-[#8b3fa8]" />
                                Fotografía de Respaldo
                            </span>
                            <button
                                type="button"
                                onClick={() => setVerFotoModalUrl(null)}
                                className="p-1 hover:bg-white/10 rounded-lg text-white transition"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-4 flex items-center justify-center bg-black/95">
                            <img
                                src={verFotoModalUrl}
                                alt="Foto de respaldo de correspondencia"
                                className="max-w-full max-h-[70vh] object-contain rounded-lg"
                            />
                        </div>
                        <div className="p-3 bg-gray-900 flex justify-end gap-2">
                            <a
                                href={verFotoModalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-3 py-1.5 text-xs font-bold text-white bg-[#61257b] hover:bg-[#8f1f64] rounded-xl transition flex items-center gap-1"
                            >
                                <ExternalLink className="w-3.5 h-3.5" />
                                Abrir original
                            </a>
                            <button
                                type="button"
                                onClick={() => setVerFotoModalUrl(null)}
                                className="px-3 py-1.5 text-xs font-semibold text-gray-300 hover:text-white bg-white/10 hover:bg-white/20 rounded-xl transition"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Gestión Afiliación Miro */}
            {modalGestionAfiliacionOpen && afiliacionCliente && (
                <GestionAfiliacionModal
                    afiliacion={afiliacionCliente}
                    onClose={() => setModalGestionAfiliacionOpen(false)}
                    onUpdated={(act) => setAfiliacionCliente(act)}
                />
            )}

            {/* Modal Nueva Afiliación */}
            {modalNuevaAfiliacionOpen && (
                <NuevaAfiliacionModal
                    onClose={() => setModalNuevaAfiliacionOpen(false)}
                    onCreated={(nueva) => {
                        setAfiliacionCliente(nueva);
                        recargarAfiliacion();
                    }}
                    clienteInicial={usuario}
                />
            )}

            {/* Modal Pago de Domiciliación */}
            {modalPagoAfiliacionOpen && afiliacionCliente && (
                <PagoAfiliacionModal
                    isOpen={modalPagoAfiliacionOpen}
                    onClose={() => setModalPagoAfiliacionOpen(false)}
                    afiliacion={afiliacionCliente}
                    onPagoSuccess={() => recargarAfiliacion()}
                />
            )}

            {/* Modal Ver Declaración Jurada */}
            {modalVerDeclaracionOpen && (
                <ModalVerDeclaracionJurada
                    isOpen={modalVerDeclaracionOpen}
                    onClose={() => setModalVerDeclaracionOpen(false)}
                    cliente={{
                        nombre: `${usuario?.first_name ?? ''} ${usuario?.last_name ?? ''}`.trim() || 'Cliente',
                        email: usuario?.email,
                        telefono: usuario?.telefono || clienteObj?.telefono,
                        ci_nit: usuario?.ci_nit || clienteObj?.ci_nit,
                        tipo_cliente: usuario?.tipo_nivel_usuario?.nombre,
                        fecha_declaracion_jurada: fechaDeclaracion,
                        foto_declaracion_jurada_url: fotoDeclaracionUrl
                    }}
                    fotoUrl={fotoDeclaracionUrl}
                    clienteNombre={`${usuario?.first_name ?? ''} ${usuario?.last_name ?? ''}`.trim()}
                    fechaFirma={fechaDeclaracion ?? undefined}
                    tipoNivel={usuario?.tipo_nivel_usuario?.nombre}
                    ci_nit={usuario?.ci_nit ?? clienteObj?.ci_nit ?? undefined}
                    telefono={usuario?.telefono ?? clienteObj?.telefono ?? undefined}
                />
            )}

            {/* Modal Subir / Actualizar Declaración Jurada */}
            {modalSubirDeclaracionOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-[#8f1f64] to-[#61257b] px-6 py-5 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-white/15">
                                    <FileCheck className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-white font-bold text-base">Declaración Jurada</h2>
                                    <p className="text-white/75 text-xs">{isDeclaracionFirmada ? 'Actualizar firma registrada' : 'Registrar firma del cliente'}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setModalSubirDeclaracionOpen(false); setArchivoDeclaracion(null); }}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            {isDeclaracionFirmada && (
                                <div className="mb-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex items-start gap-3">
                                    <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-semibold text-emerald-800">Declaración ya registrada</p>
                                        <p className="text-xs text-emerald-600 mt-0.5">
                                            {fechaDeclaracion ? `Firmada el ${new Date(fechaDeclaracion).toLocaleDateString('es-ES')}` : 'Firma registrada'}
                                        </p>
                                    </div>
                                </div>
                            )}

                            <DeclaracionJuradaUpload
                                onFileSelected={(file) => setArchivoDeclaracion(file)}
                                yaFirmada={isDeclaracionFirmada}
                                fotoActualUrl={fotoDeclaracionUrl ?? undefined}
                            />

                            <div className="mt-6 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setModalSubirDeclaracionOpen(false); setArchivoDeclaracion(null); }}
                                    className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleGuardarDeclaracion}
                                    disabled={!archivoDeclaracion || guardandoDeclaracion}
                                    className="flex-1 py-2.5 px-4 rounded-xl bg-gradient-to-r from-[#8f1f64] to-[#61257b] text-white text-sm font-bold hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {guardandoDeclaracion ? (
                                        <>
                                            <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <FileCheck className="h-4 w-4" />
                                            {isDeclaracionFirmada ? 'Actualizar Firma' : 'Guardar Firma'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DetalleKardexClientesPage;


